import { FilterService } from 'src/app/filter/filter.service';
import { EventEmitter, HostListener, Injectable, OnDestroy, Output } from '@angular/core'
import { Subject } from 'rxjs';
import { GraphicsRenderDecal } from '../digital-twin/graphics/graphics-render-decal'
import { CollectionService } from '../collections/collection.service'
import {
  InspectionScope,
} from 'projects/phdsoft/common/src/lib/models/inspection-scope'
import { ASSET_DEFAULTS, DocumentMediaModel, GeometryTypeConstants, Util } from '@phdsoft/common'
import { AssetManager, FunctionalityMode } from './asset-manager'
import * as THREE from '@phdsoft/three'
import { Graphics } from '../digital-twin/graphics/graphics'
import { InspectionCampaign } from '../platform/busines-data/Campaigns/InspectionCampaign'
import { WorkableElement } from '../platform/busines-data/SectionElements/WorkableElement'
import { SteelThicknessGaugingPoint } from '../platform/busines-data/SectionElements/InspectionElements/steelThicknessGaugingPoint'
import { GaugingPoint } from '../platform/busines-data/SectionElements/InspectionElements/gaugingPoint'
import { GraphicsMaterials } from '../digital-twin/graphics/graphics-materials'
import { ElementResultInfo, OfflineInspectionStatus } from 'projects/phdsoft/common/src/lib/models/element-result-info.model'
import { v4 as uuid } from 'uuid'
import { GaugingElement } from '../platform/busines-data/SectionElements/InspectionElements/gaugingElement'
import { ChecklistItem } from 'src/app/collections/collection-inspection/_models/checklist.model'
import { StructuralElement } from '../platform/busines-data/SectionElements/StructureElements/StructuralElement'
import { MatSnackBar } from '@angular/material/snack-bar';
import { Mesh, Vector3 } from '@phdsoft/three'
import { GraphicsRenderRepair } from '../digital-twin/graphics/graphics-render-repair'
import { INSERTIONCONSTANTS } from 'projects/phdsoft/common/src/lib/shared/constants/insertions-constants'
import { ElementStatus } from '../platform/busines-data/SectionElements/InspectionElements/elementStatus'
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import { INSPECTIONS_TYPES_IDS } from 'projects/phdsoft/common/src/lib/shared/constants/inspections-types-ids';
import { MatDialog } from '@angular/material/dialog';
import { TemplateService } from '@app/template/template.service';
import { FileUploadService } from '@app/admin/services/file-upload.service';
import { AssetOfflineService } from '@app/shared/services/asset-offline.service';
import { AssetManagerInspectionDrag } from './asset-manager-inspection-drag';
import { AssetManagerInspectionRetarget } from './asset-manager-inspection-retarget';
import { InspectionPointInstanceManager } from '../digital-twin/graphics/inspection-point-instance-manager.service';
import { PointShapeType } from '../digital-twin/graphics/point-shape-geometry-cache';
import { PhantomSurfaceDesign } from '../digital-twin/graphics/phantom-surface-design';
import { Section } from '../platform/busines-data/Framework/Section';
import { PivotAxisLock } from './pivot-axis-lock';
import { InspectionCommandService } from '@app/shared/command/inspection/inspection-command.service';
import { AnomalyService } from '@app/admin/services/anomaly.service';
import { AnomalyModel } from 'projects/phdsoft/common/src/lib/models/anomaly.model';
import { AnomalyGeometryModel } from 'projects/phdsoft/common/src/lib/models/anomaly-geometry.model';
import { InspectionEventsService } from '@app/template/view-mode-tools/view-mode-anomaly-labels/inspection-events.service';
import { InsertionPlanChoiceService } from '@app/collections/inspection-plan-assistant/insertion-plan-choice.service';
import { calendarDay, dayToDate } from '@app/hands-free-inspection/services/hands-free-plan';

/** What a spoken reading changed, so it can be taken back exactly. */
export interface SpokenReadingUndo {
  previousValue?: number;
  items: { itemId: string; value: string; status: ElementStatus; date: Date }[];
  measurementIds: string[];
  /** The day the reading was saved under, so taking it back recounts the same plan. */
  savedOn: Date;
  /** The plan the reading was written for, when there was one. */
  planId?: string;
}

/** A spoken reading with nowhere to go: its plan occurrence has no open rows. Nothing was changed. */
export class SpokenReadingRefused extends Error {}

export interface PointDirection {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
}

@Injectable({ providedIn: 'root' })
export class AssetManagerInspection implements OnDestroy {

  private pointMethodId: string = INSERTIONCONSTANTS.POINT
  private elementInspectionMethodId: string = INSERTIONCONSTANTS.ELEMENT

  private previewPoints: THREE.Mesh[] = [];
  private previewPointsData: {
    originalPoints: any[],
    sectionId: string,
    regionIds: Array<string>,
    scope: string,
    distance: number,
    inspectionTypeId: string,
    previewIntersections: Array<{
      point: THREE.Vector3,
      normal: THREE.Vector3,
      targetElement: WorkableElement,
      label: string,
      previewMesh: THREE.Mesh
    }>
  } | null = null;

  // Drag handler (extracted to separate file for organization)
  private inspectionDrag: AssetManagerInspectionDrag;

  // "Change inspection target" controller (INSPECSELECTCHANGE / INSPECCHANGETARGET)
  private inspectionRetarget: AssetManagerInspectionRetarget;

  //#region Centralized getters/setters using asset-manager

  private get _currentInspectionCollection(): InspectionCampaign {
    return this.assetManager.__currentInspectionCollection
  }

  private set _currentInspectionCollection(value: InspectionCampaign) {
    this.assetManager.__currentInspectionCollection = value
  }

  private get referenceDate(): Date {
    return this.assetManager.referenceDate
  }

  private get currentRegionId(): string {
    return this.assetManager.getCurrentRegionId()
  }

  private get isAreaInsertion(): boolean {
    return this.assetManager.IsAreaInsertion
  }

  private set isAreaInsertion(value: boolean) {
    this.assetManager.IsAreaInsertion = value
  }

  private get functionalityMode(): FunctionalityMode {
    return this.assetManager.functionalityMode
  }

  private set functionalityMode(value: FunctionalityMode) {
    this.assetManager.functionalityMode = value
  }

  public get CollectionComponentEnableCurrentType(): boolean {
    return this.assetManager.CollectionComponentEnableCurrentType
  }
  public set CollectionComponentEnableCurrentType(value: boolean) {
    this.assetManager.CollectionComponentEnableCurrentType = value
  }

  private get elementTypeForInsertionAndSelection(): string {
    return this.assetManager.elementTypeForInsertionAndSelection
  }

  private set elementTypeForInsertionAndSelection(value: string) {
    this.assetManager.elementTypeForInsertionAndSelection = value
  }

  
  private get auxiliaryOptionForInsertionAndSelection(): string {
    return this.assetManager.auxiliaryOptionForInsertionAndSelection
  }

  private set auxiliaryOptionForInsertionAndSelection(value: string) {
    this.assetManager.auxiliaryOptionForInsertionAndSelection = value
  }

  // Drag state getters (delegated to inspectionDrag)
  public get isPointDragging(): boolean {
    return this.inspectionDrag?.isPointDragging ?? false;
  }

  public get currentDraggedPoint(): GaugingElement | null {
    return this.inspectionDrag?.currentDraggedPoint ?? null;
  }

  public get retarget(): AssetManagerInspectionRetarget {
    return this.inspectionRetarget;
  }

  //#endregion

  constructor(
    private graphicsRenderDecal: GraphicsRenderDecal,
    private graphicsRenderRepair: GraphicsRenderRepair,
    private graphics: Graphics,
    private collectionService: CollectionService,
    private assetManager: AssetManager,
    private snackBar: MatSnackBar,
    private filterService: FilterService,
    private fileUploadService: FileUploadService,
    private dialog: MatDialog,
    private templateService: TemplateService,
    private geometryTypeConstants: GeometryTypeConstants,
    private assetOfflineService: AssetOfflineService,
    private instanceManager: InspectionPointInstanceManager,
    private anomalyService: AnomalyService,
    private inspectionEventsService: InspectionEventsService,
    private insertionPlanChoice: InsertionPlanChoiceService,
    // private inspectionCommandService: InspectionCommandService,
  ) {
    this.assetManager.excessiveCorrosionCheckRequested.subscribe(inspectionElement => {
      if (inspectionElement) {
        this.checkForExcessiveCorrosionAndCreateAdditionalPoints(inspectionElement);
      }
    });

    // Initialize drag handler
    this.inspectionDrag = new AssetManagerInspectionDrag();
    this.inspectionDrag.initialize(
      this.graphics,
      this.graphicsRenderDecal,
      this.assetManager,
      this.collectionService,
      this.snackBar,
      () => this.referenceDate,
      (element) => this.prepareInspectionBeforeSaving(element),
      this.instanceManager
    );

    this.inspectionRetarget = new AssetManagerInspectionRetarget();
    this.inspectionRetarget.initialize(
      this.graphics,
      this.graphicsRenderDecal,
      this.assetManager,
      this.collectionService,
      this.snackBar,
      (element) => this.prepareInspectionBeforeSaving(element),
      this.inspectionEventsService,
      this.instanceManager
    );
  }

  @HostListener('unloaded')
  ngOnDestroy(): void {
    console.log('💣 Destroy AssetManagerInspection')
  }

  public startInspectionCollectionMode(): void {
    this.functionalityMode = 'INSPECTIONINSERTION'
    this.clearPivotPoint()
    this.graphicsRenderRepair.forceRectangleShape();
    this.assetManager.tempInsertedPointsArray = []
    this.graphics.Controls.enableRotate = false
    this.CollectionComponentEnableCurrentType = true
  }

  public addInspectionCollection(
    pDate?: Date,
    CurrentMethodId?: string,
    CurrentInspectionTypeId?: string,
    InpectionTypeName?: string,
  ): void {
    pDate = pDate ? pDate : this.referenceDate
    this.elementTypeForInsertionAndSelection = 'Plate'

    this._currentInspectionCollection = this.assetManager.asset.InspectionCampaigns[0]
    this.collectionService.changeCurrentCollection(
      this.assetManager.asset.InspectionCampaigns[0].CollectionInfoModel,
    )

    if (CurrentMethodId === INSERTIONCONSTANTS.AREA) {
      this.startInspectionCollectionAreaMethod()
      this.snackBar.open('Tap and drag on the desired area to start it.', '', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        duration: 7000
      });
    }
    else if (CurrentMethodId === this.pointMethodId && this.graphics.Controls.enableRotate) {
      this.snackBar.open('Tap and Hold to insert a point manually to start it.', '', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        duration: 7000
      });
    }
    else if (CurrentMethodId === this.elementInspectionMethodId && this.graphics.Controls.enableRotate) {
      this.snackBar.open('Tap and hold on the desired element to start it.', '', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        duration: 7000
      });
    }
  }

  private startInspectionCollectionAreaMethod() {
    this.graphics.hammerManager.get('pan').set({ enable: true })
    this.graphics.Controls.enableRotate = false
    this.isAreaInsertion = true
  }

  //#region Start Insert Inspection
  public StartInsertInspection(ev, sectionId: string, regionIds: Array<string>): void {
    if (this.functionalityMode === 'INSPECTIONINSERTION' && !this.isAreaInsertion) {
      // this.currentInspectionCollection._visible = true;
      //this._currentInspectionCollection.setVisibilityForAll(true);

      if (
        !Util.isNullOrUndefined(this._currentInspectionCollection) &&
        !Util.isNullOrUndefined(this._currentInspectionCollection.CollectionInfoModel)
      ) {
        switch (this._currentInspectionCollection.CollectionInfoModel.CurrentMethodId) {
          case INSERTIONCONSTANTS.AREA: ///Area
            break
          case INSERTIONCONSTANTS.POINT: ///Point General
            this.InsertInspectionPointMethod(ev, sectionId, regionIds)
            break
          case INSERTIONCONSTANTS.ELEMENT: ///Element
            this.InsertInspectionElementMethod(ev)
            break
          default:
            break
        }
      }
    }
  }

  public setPivotPoint(): void {
    this.releasePivotProxy();
    this.pivotPoint = this.assetManager.currentSelectedElement as SteelThicknessGaugingPoint
    // The pivot outline is pinned so ordinary selection/deselection cannot wipe it:
    // the lock stays valid exactly as long as the pivot stays visibly outlined.
    // Instanced points carry a PhantomSurfaceDesign the outline pass cannot outline
    // (its traverse() is a stub) — pin a pooled proxy mesh at the instance's
    // transform instead, the same SEL-04 mechanism the primary selection uses.
    const surface = this.pivotPoint?.SurfaceDesign;
    const outlineTarget = surface instanceof PhantomSurfaceDesign
      ? this.instanceManager.acquireProxy(this.pivotPoint, this.graphics.scene) ?? surface
      : surface;
    this.graphics.pinSecondaryOutline(outlineTarget ? [outlineTarget as THREE.Object3D] : []);
  }

  public clearPivotPoint(): void {
    this.releasePivotProxy();
    this.pivotPoint = null;
    this.assetManager.auxiliaryOptionForInsertionAndSelection = '';
    this.graphics.clearPinnedSecondaryOutline();
    this.graphics.clearOutlinedElements(true);
  }

  /** Return the pivot's outline proxy to the pool (no-op for real-mesh SurfaceDesigns). */
  private releasePivotProxy(): void {
    const previous = this.pivotPoint;
    if (!(previous?.SurfaceDesign instanceof PhantomSurfaceDesign)) return;
    this.instanceManager.releaseProxy(previous);
  }

  public get hasPivotPoint(): boolean {
    return this.pivotPoint != null;
  }

  // Inspection point drag-and-drop methods (delegated to AssetManagerInspectionDrag)
  public startInspectionPointDrag(inspectionPoint: GaugingElement): void {
    this.inspectionDrag.startInspectionPointDrag(inspectionPoint);
  }

  public updateInspectionPointPosition(ev: any): void {
    this.inspectionDrag.updateInspectionPointPosition(ev);
  }

  public async finishInspectionPointMove(): Promise<void> {
    return this.inspectionDrag.finishInspectionPointMove();
  }

  public cancelInspectionPointDrag(): void {
    this.inspectionDrag.cancelInspectionPointDrag();
  }

  private checkPositionBasedOnPivotPoint(ev: any, sectionId?: string): void {
    const lockOption = this.assetManager.auxiliaryOptionForInsertionAndSelection;
    if (!this.pivotPoint || (lockOption !== 'Lock Vertical' && lockOption !== 'Lock Horizontal')) {
      return;
    }
    const mode = lockOption === 'Lock Vertical' ? 'vertical' as const : 'horizontal' as const;
    const pivotWorldPosition = this.pivotPoint.SurfaceDesign.position.clone();

    // Gravity-based lock (ship models are Z-up): 'Lock Vertical' keeps the point on the
    // vertical world line through the pivot, 'Lock Horizontal' at the pivot's height —
    // camera-independent, computed on the CLICKED element (the old clamp to the pivot's
    // screen column/row drifted on oblique views). Probe the click with the same raycast
    // the insertion uses.
    const probeType = this.elementTypeForInsertionAndSelection === 'Solid'
      ? 'Plate'
      : this.elementTypeForInsertionAndSelection;
    const probe = this.graphicsRenderDecal.probeInsertionPoint(
      ev, probeType, this.assetManager.asset, sectionId);
    if (!probe || !probe.point) return;

    // Line elements (stiffener/bracket/faceplate lines) are corrected along their own
    // line to the pivot's station; when the station falls beyond the clicked segment,
    // the sibling element of the same stiffener/bracket (same Full/Complete id) that
    // contains the station takes over. Surface elements move within their plane.
    // The raycast's per-triangle face normal is unreliable (edge/end faces of web
    // boxes, object-space quirks): a wrong normal silently disqualifies the real lock
    // axis and the fallback axis can be a no-op on the element's plane. Prefer the
    // clicked ELEMENT's own normal — stable and world-space, the same source the ortho
    // area projection uses.
    const clickedElement = this.assetManager.asset.getStructureAndRepairsElements(true)
      .find(e => e.Guid === probe.target?.name);
    const surfaceNormal = clickedElement?.elementNormal ??
      clickedElement?.section?.sectionNormal ?? probe.normal;
    const lineEnds = this.getElementLineEnds(probe.target);
    const isLinearType = clickedElement?.TypeId === ELEMENTSTYPESIDS.STIFFENER ||
      clickedElement?.TypeId === ELEMENTSTYPESIDS.BRACKET ||
      clickedElement?.TypeId === ELEMENTSTYPESIDS.FACEPLATE;
    let lockedWorld: THREE.Vector3 = null;
    if (this.isDeckLock(clickedElement)) {
      // Near-horizontal deck with the pivot on it: both gravity constraints degenerate
      // (the vertical line meets the deck AT the pivot; every deck point already has the
      // pivot's height). The lock is instead a PLAN line through the pivot along the
      // in-plane world axis that runs most vertically on screen — for plates and
      // stiffener webs alike; a web parallel to the line cannot lock (null → unmodified).
      const planVertical = PivotAxisLock.screenVerticalPlanAxis(this.graphics.camera, pivotWorldPosition);
      lockedWorld = PivotAxisLock.lockedPlanPoint(pivotWorldPosition, probe.point, surfaceNormal, mode, planVertical);
      if (!lockedWorld) return;
    } else if (lineEnds) {
      lockedWorld = this.lockedWorldOnLineElement(lineEnds, probe.target, pivotWorldPosition, mode);
    } else if (mode === 'vertical' && isLinearType) {
      // Mesh-rendered stiffeners/brackets/faceplates (no Line2, no Line data): the
      // locked point is where the pivot's vertical world line crosses the clicked
      // element's REAL geometry — or the same-Full/Complete-id sibling segment the
      // line passes through. Exact, so curvature cannot break it (the plane math
      // below relies on the AVERAGED element normal, which lies on curved webs).
      lockedWorld = this.verticalLineOnMeshElement(clickedElement, pivotWorldPosition, probe.point);
    }
    if (!lockedWorld) {
      lockedWorld = PivotAxisLock.lockedWorldPoint(pivotWorldPosition, probe.point, surfaceNormal, mode);
    }
    if (!lockedWorld) return;

    const lockedScreen = PivotAxisLock.toScreen(this.graphics.camera, lockedWorld);
    if (!lockedScreen) return;
    const lockedX = lockedScreen.x + this.graphics.offsetX;
    const lockedY = lockedScreen.y + this.graphics.offsetY;

    // Never point the insertion at empty space or another stiffener: accept the lock
    // only when the corrected position hits the clicked element (or, for stiffener-like
    // elements, its Full/Complete-id group — the type check alone lets other stiffeners'
    // webs/flanges steal the point). The exact locked pixel often sits on the web's
    // EDGE (flare closest-approach), where the camera ray grazes past the thin mesh or
    // hits the shell behind it — so search a small screen ring before giving up; a few
    // pixels are negligible for the station but land the ray on the web face.
    const groupGuids = isLinearType
      ? [probe.target?.name, ...this.sameFullIdSiblings(clickedElement).map(e => e.Guid)]
      : null;
    const ringOffsets = [
      [0, 0],
      [2, 0], [-2, 0], [0, 2], [0, -2], [2, 2], [2, -2], [-2, 2], [-2, -2],
      [5, 0], [-5, 0], [0, 5], [0, -5], [5, 5], [5, -5], [-5, 5], [-5, -5],
      [8, 0], [-8, 0], [0, 8], [0, -8], [8, 8], [8, -8], [-8, 8], [-8, -8],
    ];
    let verify = null;
    let verifiedX = lockedX;
    let verifiedY = lockedY;
    for (const [dx, dy] of ringOffsets) {
      const candidate = this.graphicsRenderDecal.probeInsertionPoint(
        { clientX: lockedX + dx, clientY: lockedY + dy }, probeType, this.assetManager.asset, sectionId);
      if (!candidate || !candidate.point) continue;
      if (groupGuids && !groupGuids.includes(candidate.target?.name)) continue;
      verify = candidate;
      verifiedX = lockedX + dx;
      verifiedY = lockedY + dy;
      break;
    }
    if (!verify || !verify.point) return;

    const isTouchEvent = ev.changedTouches ? true : false;
    const touchEvent = isTouchEvent ? ev.changedTouches[0] : null;
    if (isTouchEvent && touchEvent) {
      if (touchEvent.center) {
        touchEvent.center.x = verifiedX;
        touchEvent.center.y = verifiedY;
      }
      if (touchEvent.clientX !== undefined) touchEvent.clientX = verifiedX;
      if (touchEvent.clientY !== undefined) touchEvent.clientY = verifiedY;
    } else {
      ev.clientX = verifiedX;
      ev.clientY = verifiedY;
      if (ev.center) {
        ev.center.x = verifiedX;
        ev.center.y = verifiedY;
      }
    }
  }

  /**
   * Deck lock applies when the clicked element's SECTION is near-horizontal (normal within
   * ~11 deg of Z, the same threshold as PivotAxisLock's in-plane reach) AND the pivot sits
   * on that same section. A pivot on another section (e.g. shell pivot, deck click in
   * multi-section mode) keeps the gravity rule: 'Lock Vertical' still means directly
   * under the pivot.
   */
  private isDeckLock(clickedElement: WorkableElement): boolean {
    const n = clickedElement?.section?.sectionNormal;
    if (!n || !this.pivotPoint || this.pivotPoint.SectionId !== clickedElement.SectionId) return false;
    const len = Math.sqrt(n.x * n.x + n.y * n.y + n.z * n.z);
    return len > 1e-12 && Math.abs(n.z) / len >= 0.98;
  }

  /**
   * 'Lock Vertical' for mesh-rendered linear elements: the point where the pivot's
   * vertical world line crosses the clicked element's mesh, or — when the clicked
   * segment does not reach that station — the sibling segment (same Full/Complete
   * stiffener/bracket id, same section) that the line passes through.
   */
  private verticalLineOnMeshElement(
    clicked: WorkableElement,
    pivotWorld: THREE.Vector3,
    nearTo: THREE.Vector3,
  ): THREE.Vector3 | null {
    const candidates = [clicked, ...this.sameFullIdSiblings(clicked)];
    let best: { point: THREE.Vector3; horizontalDistance: number } = null;
    for (const candidate of candidates) {
      const approach = this.verticalLineClosestApproachOnMesh(candidate, pivotWorld, nearTo);
      if (approach && (!best || approach.horizontalDistance < best.horizontalDistance)) {
        best = approach;
      }
    }
    return best?.point ?? null;
  }

  /**
   * Best approach of the pivot's vertical world line to the element's mesh: the exact
   * crossing when the line pierces the mesh (horizontalDistance 0), otherwise the mesh
   * point horizontally closest to the line. The latter is essential on flared hulls:
   * a stiffener above/below the pivot sits at a different Y for the same X, so the
   * strict vertical line misses its mesh entirely — the correct station is still the
   * horizontally-closest point, exactly like the old Line2 least-squares station.
   */
  private verticalLineClosestApproachOnMesh(
    element: WorkableElement,
    pivotWorld: THREE.Vector3,
    nearTo: THREE.Vector3,
  ): { point: THREE.Vector3; horizontalDistance: number } | null {
    const mesh = element?.SurfaceDesign;
    const geometry = mesh?.geometry as THREE.BufferGeometry;
    const position = geometry?.attributes?.position;
    if (!position) return null;
    mesh.updateWorldMatrix(true, false);

    const box = new THREE.Box3().setFromObject(mesh);
    if (box.isEmpty()) return null;
    if (pivotWorld.x >= box.min.x && pivotWorld.x <= box.max.x &&
      pivotWorld.y >= box.min.y && pivotWorld.y <= box.max.y) {
      const origin = new THREE.Vector3(pivotWorld.x, pivotWorld.y, box.max.z + 1);
      const raycaster = new THREE.Raycaster(
        origin, new THREE.Vector3(0, 0, -1), 0, (box.max.z - box.min.z) + 2);
      const hits = raycaster.intersectObject(mesh, true);
      if (hits.length > 0) {
        // the line can cross the element more than once: take the crossing closest to the click
        hits.sort((h1, h2) => h1.point.distanceTo(nearTo) - h2.point.distanceTo(nearTo));
        return { point: hits[0].point.clone(), horizontalDistance: 0 };
      }
    }

    // No pierce: closest horizontal approach over the triangle edges, in world space.
    // (When the projected line falls outside the projected mesh, the minimizer lies on
    // an edge, so scanning edges is sufficient — interior minima are the pierce case.)
    const index = geometry.index;
    const triangleCount = index ? index.count / 3 : position.count / 3;
    const va = new THREE.Vector3();
    const vb = new THREE.Vector3();
    const vc = new THREE.Vector3();
    const edgePoint = new THREE.Vector3();
    let bestDistSq = Infinity;
    let bestTieBreak = Infinity;
    let bestPoint: THREE.Vector3 = null;
    const considerEdge = (p: THREE.Vector3, q: THREE.Vector3) => {
      const ex = q.x - p.x;
      const ey = q.y - p.y;
      const lenSq = ex * ex + ey * ey;
      let t = lenSq > 1e-12
        ? ((pivotWorld.x - p.x) * ex + (pivotWorld.y - p.y) * ey) / lenSq
        : 0;
      t = Math.max(0, Math.min(1, t));
      edgePoint.lerpVectors(p, q, t);
      const dx = edgePoint.x - pivotWorld.x;
      const dy = edgePoint.y - pivotWorld.y;
      const distSq = dx * dx + dy * dy;
      const tieBreak = edgePoint.distanceToSquared(nearTo);
      if (distSq < bestDistSq - 1e-12 ||
        (distSq < bestDistSq + 1e-12 && tieBreak < bestTieBreak)) {
        bestDistSq = distSq;
        bestTieBreak = tieBreak;
        bestPoint = edgePoint.clone();
      }
    };
    for (let i = 0; i < triangleCount; i++) {
      const ia = index ? index.getX(i * 3) : i * 3;
      const ib = index ? index.getX(i * 3 + 1) : i * 3 + 1;
      const ic = index ? index.getX(i * 3 + 2) : i * 3 + 2;
      va.fromBufferAttribute(position as THREE.BufferAttribute, ia).applyMatrix4(mesh.matrixWorld);
      vb.fromBufferAttribute(position as THREE.BufferAttribute, ib).applyMatrix4(mesh.matrixWorld);
      vc.fromBufferAttribute(position as THREE.BufferAttribute, ic).applyMatrix4(mesh.matrixWorld);
      considerEdge(va, vb);
      considerEdge(vb, vc);
      considerEdge(vc, va);
    }
    if (!bestPoint) return null;
    return { point: bestPoint, horizontalDistance: Math.sqrt(bestDistSq) };
  }

  /** Elements of the same stiffener/bracket group (same grouping as findSiblingStationPoint). */
  private sameFullIdSiblings(clicked: WorkableElement): WorkableElement[] {
    const general = clicked?.properties?.General;
    const groupId = general?.FullStiffenerId ?? general?.CompleteStiffenerId ??
      general?.FullBracketId ?? general?.CompleteBracketId;
    if (Util.isNullOrUndefined(groupId)) return [];
    return this.assetManager.asset.getStructureElements(true).filter(e =>
      e !== clicked && e.SectionId === clicked.SectionId && (
        e.properties?.General?.FullStiffenerId === groupId ||
        e.properties?.General?.CompleteStiffenerId === groupId ||
        e.properties?.General?.FullBracketId === groupId ||
        e.properties?.General?.CompleteBracketId === groupId));
  }

  /** Start/end of a Line2-style element representation (null for surface meshes). */
  private getElementLineEnds(target: any): { start: THREE.Vector3; end: THREE.Vector3 } | null {
    const arr = target?.geometry?.attributes?.instanceEnd?.array;
    if (!arr || arr.length < 6) return null;
    return {
      start: new THREE.Vector3(arr[0], arr[1], arr[2]),
      end: new THREE.Vector3(arr[3], arr[4], arr[5]),
    };
  }

  private lockedWorldOnLineElement(
    lineEnds: { start: THREE.Vector3; end: THREE.Vector3 },
    target: any,
    pivotWorld: THREE.Vector3,
    mode: 'vertical' | 'horizontal',
  ): THREE.Vector3 | null {
    const station = PivotAxisLock.stationOnLineForMode(
      lineEnds.start, lineEnds.end, pivotWorld, mode);
    if (!station) return null;
    if (station.u >= -0.001 && station.u <= 1.001) return station.point;
    // station beyond this segment — hand over to the sibling element containing it
    return this.findSiblingStationPoint(target, pivotWorld, mode, station.point);
  }

  /**
   * Point at the locked station on the sibling element of the same stiffener/bracket
   * (matching Full/Complete id, like createMatchingFlangesPoints) whose line span
   * contains it — for clicks on a segment that does not reach the pivot's station.
   */
  private findSiblingStationPoint(
    target: any,
    pivotWorld: THREE.Vector3,
    mode: 'vertical' | 'horizontal',
    nearTo: THREE.Vector3,
  ): THREE.Vector3 | null {
    const elements: WorkableElement[] = this.assetManager.asset.getStructureElements(true);
    const clicked = elements.find(e => e.Guid === target?.name);
    const general = clicked?.properties?.General;
    const groupId = general?.FullStiffenerId ?? general?.CompleteStiffenerId ??
      general?.FullBracketId ?? general?.CompleteBracketId;
    if (Util.isNullOrUndefined(groupId)) return null;

    let best: THREE.Vector3 = null;
    let bestDistance = Infinity;
    for (const element of elements) {
      if (element === clicked || element.SectionId !== clicked.SectionId) continue;
      const g = element.properties?.General;
      if (g?.FullStiffenerId !== groupId && g?.CompleteStiffenerId !== groupId &&
        g?.FullBracketId !== groupId && g?.CompleteBracketId !== groupId) continue;
      const lineEnds = this.getElementLineEnds(element.SurfaceDesign);
      if (!lineEnds) continue;
      const station = PivotAxisLock.stationOnLineForMode(lineEnds.start, lineEnds.end, pivotWorld, mode);
      if (!station || station.u < -0.001 || station.u > 1.001) continue;
      // several siblings can contain the station (web + flange lines): take the one
      // closest to the clicked line's own extension
      const distance = station.point.distanceTo(nearTo);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = station.point;
      }
    }
    return best;
  }

  private pivotPoint: SteelThicknessGaugingPoint = null;
  
  // Track the last inserted inspection element (not using tempInsertedPointsArray)
  private _lastInsertedInspection: GaugingElement = null;
  public get lastInsertedInspection(): GaugingElement {
    return this._lastInsertedInspection;
  }

  // Subject to emit when a new inspection is created
  public inspectionCreated$ = new Subject<GaugingElement>();

  private async InsertInspectionPointMethod(ev, sectionId: string, regionIds: Array<string>) {
    let counter = 0
    var box = this.assetManager.asset.GetStructureVisibleRegions()[0].sections.find(x => x.Guid === sectionId).box3
    let _inspectionPointsGeneral: any[] = null
    let scope: InspectionScope
    let resultElement: ElementResultInfo;
    let type: string = null;
    let pointNormal: any = {normal: null};

    this.checkPositionBasedOnPivotPoint(ev, sectionId);

    switch (this.elementTypeForInsertionAndSelection) {
      case 'Plate':
      case 'Solid':
        _inspectionPointsGeneral = this.graphicsRenderDecal.insertPlatePoint(
          ev,
          box,
          this.assetManager.asset,
          sectionId,
          pointNormal
        )
        scope = 'ONLY PLATE'
        break
      case 'Stiffener':
        _inspectionPointsGeneral = this.graphicsRenderDecal.insertStiffenerPoint(
          ev,
          this.assetManager.asset,
          sectionId
        )
        type = 'Stiffener'
        scope = 'NONE'
        break
      case 'FacePlate':
        _inspectionPointsGeneral = this.graphicsRenderDecal.insertFacePlatePoint(
          ev,
          this.assetManager.asset,
          sectionId
        )
        scope = 'ONLY FACE PLATE'
        break
      case 'Bracket':
        _inspectionPointsGeneral = this.graphicsRenderDecal.insertBracketPoint(
          ev,
          this.assetManager.asset,
          sectionId
        )
        type = 'Bracket'
        scope = 'NONE'
        break
      case 'BeamPart':
        _inspectionPointsGeneral = this.graphicsRenderDecal.insertBeamPartPoint(
          ev,
          box,
          this.assetManager.asset,
          sectionId
        )
        scope = 'ONLY BEAM PART'
        break
      case 'Cable':
        _inspectionPointsGeneral = this.graphicsRenderDecal.insertCablePoint(
          ev,
          box,
          this.assetManager.asset,
          sectionId
        )
        scope = 'ONLY CABLE'
        break
      case 'Pipe':
        _inspectionPointsGeneral = this.graphicsRenderDecal.insertPipePoint(
          ev,
          box,
          this.assetManager.asset,
          sectionId
        )
        scope = 'ONLY PIPE'
        break
      default:
        break
    }
    //this.graphicsRenderDecal.insertPlatePoint(ev, box);
    if (_inspectionPointsGeneral != null && _inspectionPointsGeneral.length > 0) {
      // Asked once the click has been resolved, so moving the view while it waits cannot move
      // the points, and about the regions the points go to.
      const regionsHit = _inspectionPointsGeneral.map(point => this.findPointTarget(point, sectionId, regionIds)?.regionId)
      if (!(await this.confirmInsertionPlan(regionsHit))) return
      let resultElementsArray: Array<ElementResultInfo> = [];
      let clonedResultElementsArray: Array<ElementResultInfo> = [];
      for (const _inspectionPoint of _inspectionPointsGeneral) {
        let _target: WorkableElement = this.findPointTarget(_inspectionPoint, sectionId, regionIds)
        if (
          _inspectionPointsGeneral.length === 1 &&
          _target.properties?.Geometry?.WebThickness &&
          scope === 'NONE'
        ) {
          scope = 'ONLY WEB'
        } else if (
          _inspectionPointsGeneral.length === 1 &&
          _target.properties?.Geometry?.FlangeThickness &&
          scope === 'NONE'
        ) {
          scope = 'ONLY FLANGE'
        } else {
          scope = scope !== 'NONE' ? scope : counter === 0 ? 'ONLY WEB' : 'ONLY FLANGE'
        }

        const ElementResultInfo = this.createElementsViewModel(
          _target,
          this._currentInspectionCollection,
          scope,
        )
        if(pointNormal.normal){
          ElementResultInfo.ElementInfo.normal = [pointNormal.normal.x, pointNormal.normal.y, pointNormal.normal.z];
        }
        if (scope == "ONLY PIPE") {
          if (_target.Line?.length >= 6) {
            const i = _target.Line.length

            const start = new THREE.Vector3(_target.Line[0], _target.Line[1], _target.Line[2])
            const end = new THREE.Vector3(_target.Line[i - 3], _target.Line[i - 2], _target.Line[i - 1])
            const line = new THREE.Line3(start, end);

            const point: THREE.Vector3 = _inspectionPoint.point.position.clone()

            let ringPosition = new THREE.Vector3()
            line.closestPointToPoint(point, false, ringPosition);

            const normal = point.clone().sub(ringPosition).normalize()

            let valueNormal = new Array();
            valueNormal.push(normal.x);
            valueNormal.push(normal.y);
            valueNormal.push(normal.z);

            ElementResultInfo.ElementInfo.normal = valueNormal
          }
        }

        ElementResultInfo.CheckList = new Array<ChecklistItem>()
        // const listTypes = this.assetManager.listInpectionType.find(x => x.id === this._currentInspectionCollection.CollectionInfoModel.InspectionTypeId);
        // _otherInspection.scope = scope !== 'NONE' ? scope : counter === 0 ? 'ONLY WEB' : 'ONLY FLANGE';;

        ElementResultInfo.InspectionTypeId = this.assetManager.currentInspectionTypeId;
        ElementResultInfo.InspectionTypeAlias = this.assetManager.listInpectionType?.find(t => t.id === this.assetManager.currentInspectionTypeId)?.alias;
        let inspectionElement: GaugingElement = null
        if (
          this._currentInspectionCollection.CollectionInfoModel.InspectionTypeId ===
          INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING ||
          this._currentInspectionCollection.CollectionInfoModel.CurrentInspectionTypeId ===
          INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING
        )
          inspectionElement = new SteelThicknessGaugingPoint(
            _inspectionPoint.point,
            GraphicsMaterials.pointMaterial,
            _target,
            null,
            ElementResultInfo,
          )
        if (_target instanceof StructuralElement)
          (_target as StructuralElement).addGaugingElement(inspectionElement as GaugingElement)
        else
          inspectionElement = new GaugingPoint(
            _inspectionPoint.point,
            GraphicsMaterials.pointMaterial,
            _target,
            null,
            ElementResultInfo,
          )
        // if (_target instanceof StructuralElement) (_target as StructuralElement).addGaugingElement(inspectionElement as GaugingElement);

        // Do NOT register the point in the structure section's surfaceElements —
        // inspection elements must never appear in StructureCampaigns[0].Elements
        // (prod parity: they live only in the InspectionCampaign, whose regions are
        // searched by markElementSelected via GetVisibleRegionsAllTypes).
        // Wire into InstancedMesh for immediate visibility and raycaster resolution (REND-01, SEL-02).
        // addInstance writes the slot and composites the world-transform matrix from the real pointMesh.
        // PhantomSurfaceDesign replaces the real mesh so .visible/.material access sites work unchanged.
        // Must run BEFORE the collection addSurfaceElement: with the phantom on
        // SurfaceDesign, the `instanceof THREE.Object3D` guard in Section.addSurfaceElement
        // skips adding the real mesh to the in-scene surfaceGroup — otherwise it renders
        // as a duplicate of the InstancedMesh instance (mirrors the bulk download path).
        this._addInstanceForPointElement(
          inspectionElement as GaugingElement,
          _inspectionPoint.point,
          _target.section,
          scope,
        );
        this._currentInspectionCollection.addSurfaceElement(inspectionElement)
        // addSurfaceElement overwrites element.section with the campaign's internal
        // Section — restore the structure section (mirrors the bulk download path).
        inspectionElement.section = _target.section;
        this.assetManager.tempInsertedPointsArray.push(inspectionElement);
        this._lastInsertedInspection = inspectionElement as GaugingElement;
        this.inspectionCreated$.next(inspectionElement as GaugingElement);
        this.collectionService.addElement(
          inspectionElement,
          this._currentInspectionCollection.Date,
          false,
        )

        inspectionElement.label = await this.generateInspectionLabel(inspectionElement.elementResult?.InsertedRegionId, inspectionElement.SectionId, (inspectionElement as GaugingElement).inspectionTypeId)

        this.collectionService.changeSelectElement(inspectionElement)
        counter++;
        if (_target.TypeId != ELEMENTSTYPESIDS.PIPE) {
          scope = 'NONE';
        }

        let inspElement = (inspectionElement as GaugingElement).elementResult
        resultElementsArray.push(inspElement)
        if (!window.navigator.onLine) {
          inspElement.IsOfflineInspection = true
          inspElement.OfflineInspectionStatus = OfflineInspectionStatus.Approved
        }
        let clonedResultElement = await this.prepareInspectionBeforeSaving(inspectionElement as GaugingElement)

        clonedResultElementsArray.push(clonedResultElement)

        if (type === 'Stiffener' || type === 'Bracket') {
          await this.createMatchingFlangesPoints(_inspectionPointsGeneral, type, _inspectionPoint.point, resultElementsArray, clonedResultElementsArray)
        }
      }
      this.saveInsertedPoints(clonedResultElementsArray, resultElementsArray)

      // // Ask user if they want to add 4 additional points for thickness gauging
      // if (this._currentInspectionCollection.CollectionInfoModel.InspectionTypeId === INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING ||
      //     this._currentInspectionCollection.CollectionInfoModel.CurrentInspectionTypeId === INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING) {
      //   this.askForAdditionalPoints(_inspectionPointsGeneral, sectionId, regionIds, scope, this.assetManager.currentInspectionTypeId)
      // }
    }
  }

  /** The element a clicked point is placed on, searched in the clicked element's regions. */
  private findPointTarget(_inspectionPoint: any, sectionId: string, regionIds: Array<string>): WorkableElement {
    let _target: WorkableElement = null
    for (const structureCollection of this.assetManager.asset.StructureCampaigns) {
      if (sectionId) {
        for (const regionId of regionIds) {
          _target = structureCollection.getElementById(
            regionId,
            sectionId,
            _inspectionPoint.target.name,
          )
          if (_target) break
        }
        if (_target) break
      } else {
        for (const regionId of regionIds) {
          _target = structureCollection.getElementByIdOnly(
            regionId,
            _inspectionPoint.target.name,
          )
          if (_target) break
        }
        if (_target) break
      }
    }
    if (!_target) {
      if (sectionId) {
        for (const regionId of regionIds) {
          _target = this.assetManager.asset.RepairCampaigns[0]?.getElementById(
            regionId,
            sectionId,
            _inspectionPoint.target.name,
          )
          if (_target) break
        }
      } else {
        for (const regionId of regionIds) {
          _target = this.assetManager.asset.RepairCampaigns[0]?.getElementByIdOnly(
            regionId,
            _inspectionPoint.target.name,
          )
          if (_target) break
        }
      }
    }
    return _target
  }

  /**
   * Before inspections are placed by hand: when their day is in two or more open plans and
   * none was chosen for it, the user picks the plan. False when the user cancels, and then
   * nothing is placed.
   */
  private confirmInsertionPlan(regionIds: ReadonlyArray<string | undefined>): Promise<boolean> {
    return this.insertionPlanChoice.confirm(
      this.assetManager.asset?.assetInfo?.AssetId,
      regionIds,
      this.assetManager.currentInspectionTypeId,
      this.assetManager.referenceDate)
  }

  private async prepareInspectionBeforeSaving(inspectionElement: GaugingElement): Promise<ElementResultInfo> {
    const assetInspectionTypes = this.assetManager.listInpectionType;
    const currentType = assetInspectionTypes.find(t => t.id === inspectionElement.inspectionTypeId);
    if (
      inspectionElement.elementResult.Element.inspectionMethodId === INSERTIONCONSTANTS.POINT ||
      inspectionElement.elementResult.InspectionMethodId === INSERTIONCONSTANTS.AREA ||
      inspectionElement.elementResult.InspectionMethodId === INSERTIONCONSTANTS.ELEMENT
    ) {
      if (inspectionElement.elementResult.Element.checklist?.length == 0) {
        currentType.listRows.forEach((itemCheck) => {
          const item: ChecklistItem = {
            Id: '',
            StatusId: '',
            InputTypeId: itemCheck?.inputTypeId,
            Label: itemCheck?.name,
            Value: '',
            Attachments: [],
            Status: ElementStatus.Outlined,
            Date: this.assetManager.referenceDate,
            IsLocked: false,
            AttributesList: itemCheck?.attributesList,
            InspectionSubTypeId: itemCheck?.id,
            AnomalyId: null,
            Comments: '',
            Degree: 0,
            Recommendations: '',
            Measurements: [],
            ActionPlanId: null,
            PlanIds: [],
            Clusterized: false,
          }
          inspectionElement.elementResult.Element.checklist.push(item)
        })
        inspectionElement.elementResult.CheckList = inspectionElement.elementResult.Element.checklist;
      }
    }

    this.setGeometry(inspectionElement.elementResult)

    if (!inspectionElement.label) {
      inspectionElement.label = await this.generateInspectionLabel(inspectionElement.regionIds[0], inspectionElement.SectionId, inspectionElement.inspectionTypeId)
    }
    var targetLabels = ""
    for (let i = 0; i < inspectionElement.targetElements.length; i++) {
      let label = inspectionElement.targetElements[i].properties?.General?.Label;
      if (i == 0) {
        targetLabels = label
      } else {
        targetLabels = targetLabels + ", " + label
      }
    }
    inspectionElement.targetLabels = targetLabels
    if (!window.navigator.onLine) {
      inspectionElement.elementResult.IsOfflineInspection = true
      inspectionElement.elementResult.OfflineInspectionStatus = OfflineInspectionStatus.Approved
    }
    const clonedResultElement: ElementResultInfo = JSON.parse(
      JSON.stringify(Util.omitKeys(inspectionElement.elementResult, ['Element'])),
    )
    clonedResultElement.CheckList = inspectionElement.checklist;
    clonedResultElement.AssetId = this.assetManager.asset.assetInfo.AssetId;
    clonedResultElement.RegionIds = inspectionElement.regionIds;
    clonedResultElement.InspectionTypeId = inspectionElement.inspectionTypeId;
    clonedResultElement.InspectionTypeAlias = currentType?.alias;
    clonedResultElement.TargetLabels = inspectionElement.targetLabels;
    clonedResultElement.Label = inspectionElement.label;

    return clonedResultElement;
  }

  private saveInsertedPoints(clonedResultElementsArray: ElementResultInfo[], resultElementsArray: ElementResultInfo[]) {
    this.collectionService.saveInspectionElement(clonedResultElementsArray, this.assetManager.referenceDate, this.assetManager.listInpectionType.find(x => x.id === clonedResultElementsArray[0]?.InspectionTypeId)?.name).then((collectionId) => {
      resultElementsArray.forEach((element) => {
        if (this.assetManager.degradationMode && this.assetManager.shouldEstimateDegradationCurve(element)) {
          this.assetManager.estimateElementDegradationCurve(element.id, element.TargetGeometryId)
        }
        element.Element.saved = true;
      });

      // Register insert commands for undo/redo tracking
      resultElementsArray.forEach((element) => {
        const inspectionTypeName = this.assetManager.listInpectionType.find(
          x => x.id === element.InspectionTypeId
        )?.name;

        // this.inspectionCommandService.registerInsertCommand(
        //   element.Element as GaugingElement,
        //   this._currentInspectionCollection?.CollectionInfoModel,
        //   this.assetManager.referenceDate,
        //   inspectionTypeName
        // );
      });

      if (!this._currentInspectionCollection) {
        this._currentInspectionCollection = this.assetManager.asset.InspectionCampaigns[0]
      }

      if (Util.isNullOrUndefined(this._currentInspectionCollection.CollectionInfoModel.CollectionId)) {
        this.updateCollectionAfterSaving(collectionId);
      }

      resultElementsArray.forEach((element) => {
        element.Element.updateMaterial(this.assetManager.referenceDate);
      });

    });

    this.persistForOfflineUse(clonedResultElementsArray);
  }

  private persistForOfflineUse(elements: ElementResultInfo[], planId?: string): void {
    if (!window.navigator.onLine) {
      // This saves on the local db/storage so offline will have data even on reloads
      this.assetOfflineService.saveInspectionToOffline(
        elements,
        this.assetManager.asset.assetInfo.AssetId,
        elements[0].InsertedRegionId,
        this.assetManager.referenceDate,
        planId,
      ).then(() => {
        console.log('Inspection elements saved offline');
      }).catch((error) => {
        console.error('Error saving inspection elements offline:', error);
      });
    }
  }

  private setGeometry(resultElement: ElementResultInfo) {
    if (resultElement.Element.inspectionMethodId === INSERTIONCONSTANTS.POINT) {
      //element.Element = null;//{ XYZ: resultElement.Element.design.position, Scope: element.Element.scope, Checklist: resultElement.Element.checklist };
      resultElement.ElementInfo = {
        type: 'b976ab089c794d10ac2547dde1d6a370',
        vertices: [
          resultElement.Element.SurfaceDesign.position.x,
          resultElement.Element.SurfaceDesign.position.y,
          resultElement.Element.SurfaceDesign.position.z,
        ],
        normal: resultElement.ElementInfo.normal
      };
    } else if (resultElement.Element.inspectionMethodId === INSERTIONCONSTANTS.AREA) {
      //element.Element = null;
      resultElement.ElementInfo = {
        type: '06af6789e2ce461e9eb6b9fdfb5a8218',
        faces: [],
        vertices: [], //resultElement.Element.design.geometry.attributes.position.array.map(x => x)
        normal: resultElement.ElementInfo.normal
      };

      resultElement.Element.SurfaceDesign.geometry.attributes.position.array.forEach((v) => {
        resultElement.ElementInfo.vertices.push(v);
      });
      resultElement.Element.SurfaceDesign.geometry.index.array.forEach((i) => {
        resultElement.ElementInfo.faces.push(i);
      });
      //resultElement.ElementInfo.normal.push(a);
    }
  }

  private updateCollectionAfterSaving(collectionId: any) {
    this.assetManager.updateElementsCollectionId(
      collectionId,
      this._currentInspectionCollection.CollectionInfoModel.TempCollectionId
    );
    this.collectionService.updateCollectionId(
      this._currentInspectionCollection.CollectionInfoModel,
      collectionId
    );
    // this.currentCollection.IsSaving = false;
    // this.currentCollection.TempCollectionId = collectionId;
    // this.currentCollection.CollectionId = collectionId;
  }

  private async createMatchingFlangesPoints(
    _inspectionPointsGeneral: any,
    type: string,
    webPoint: Mesh,
    resultElementsArray: ElementResultInfo[],
    clonedResultElementsArray: ElementResultInfo[]
  ) {
    let sectionStructureElements: WorkableElement[] = this.assetManager.asset.getStructureElements(true);
    let webTarget: WorkableElement = sectionStructureElements.find(e =>
      e.Guid === _inspectionPointsGeneral[0].target.name &&
      e.properties?.Geometry?.WebThickness > 0
    );
    let completeGroupId = null;
    if (type === 'Stiffener') {
      completeGroupId = webTarget?.properties?.General?.FullStiffenerId ?? webTarget?.properties?.General?.CompleteStiffenerId;
    } else if (type === 'Bracket') {
      completeGroupId = webTarget?.properties?.General?.FullBracketId ?? webTarget?.properties?.General?.CompleteBracketId;
    }
    if (!Util.isNullOrUndefined(completeGroupId)) {
      // // compute web bounding box
      // let webBoundingBoxCenter: THREE.Vector3 = new THREE.Vector3();
      // webTarget.SurfaceDesign.geometry.computeBoundingBox();
      // webTarget.SurfaceDesign.geometry.boundingBox.getCenter(webBoundingBoxCenter);

      let matchingFlanges: WorkableElement[] = sectionStructureElements.filter(e =>
        (
          e.properties?.General?.FullStiffenerId === completeGroupId ||
          e.properties?.General?.FullBracketId === completeGroupId ||
          e.properties?.General?.CompleteStiffenerId === completeGroupId ||
          e.properties?.General?.CompleteBracketId === completeGroupId
        ) &&
        e.properties?.Geometry?.FlangeThickness > 0
      );
      let insertedAny = false;
      for (let flange of matchingFlanges) {
        let intersection = this.getClosestFlangeIntersectionPoint(webTarget, flange, webPoint)

        // add inspection point to flange
        if (intersection.length > 0) {
          await this.insertFlangeGaugingPoint(
            flange, intersection[0].point.toArray(), flange.elementNormal.normalize(),
            type, resultElementsArray, clonedResultElementsArray);
          insertedAny = true;
        }
      }

      // Curved ("with form") stiffeners: the rays are aimed along the flange's AVERAGED
      // vertex normal, which bends away from the local surface on curved geometry, so
      // every ray can miss the thin flange. Fall back to the exact closest point on the
      // flange mesh — station-correct for the parallel web/flange plates and immune to
      // normal averaging. Only the nearest group flange gets the point, mirroring the
      // locality the raycast provided implicitly.
      if (!insertedAny && matchingFlanges.length > 0) {
        let bestFlange: WorkableElement = null;
        let best: { point: THREE.Vector3; normal: THREE.Vector3; distance: number } = null;
        for (let flange of matchingFlanges) {
          const closest = this.closestPointOnElementMesh(flange, webPoint.position);
          if (closest && (!best || closest.distance < best.distance)) {
            best = closest;
            bestFlange = flange;
          }
        }
        // An adjacent flange sits about one web height from the web point; a much
        // larger distance means the rays missed legitimately (web point beyond the
        // flange's extent) and the closest point is a clamp to the flange end at a
        // wrong station — insert nothing there, like the raycast-only code did.
        const maxFallbackDistance = Math.max(0.3, 1.5 * this.estimatedProfileHeight(webTarget));
        if (best && bestFlange && best.distance <= maxFallbackDistance) {
          await this.insertFlangeGaugingPoint(
            bestFlange, best.point.toArray(), best.normal.normalize(),
            type, resultElementsArray, clonedResultElementsArray);
        }
      }
    }
  }

  private async insertFlangeGaugingPoint(
    flange: WorkableElement,
    pointPosition: number[],
    pointNormal: THREE.Vector3,
    type: string,
    resultElementsArray: ElementResultInfo[],
    clonedResultElementsArray: ElementResultInfo[],
  ): Promise<void> {
    let _inspectionPoint = this.graphicsRenderDecal.createPlatePointFromPosition(
      pointPosition,
      flange,
      type,
      'ONLY FLANGE',
      pointNormal,
      flange.section.box3
    );

    const ElementResultInfo = this.createElementsViewModel(
      flange,
      this._currentInspectionCollection,
      'ONLY FLANGE'
    );
    ElementResultInfo.CheckList = new Array<ChecklistItem>();
    ElementResultInfo.InspectionTypeId = this.assetManager.currentInspectionTypeId;
    ElementResultInfo.InspectionTypeAlias = this.assetManager.listInpectionType?.find(t => t.id === this.assetManager.currentInspectionTypeId)?.alias;
    let inspectionElement: GaugingElement = new SteelThicknessGaugingPoint(
      _inspectionPoint.point,
      GraphicsMaterials.pointMaterial,
      flange,
      null,
      ElementResultInfo
    );

    // NOT registered in the structure section's surfaceElements — inspection
    // elements must never appear in StructureCampaigns[0].Elements (prod parity).
    // Wire into InstancedMesh — matching flange points use ONLY FLANGE shape (SEL-02).
    // Must run BEFORE the collection addSurfaceElement so the phantom prevents the
    // real mesh from being added to the in-scene surfaceGroup (duplicate rendering).
    this._addInstanceForPointElement(
      inspectionElement as GaugingElement,
      _inspectionPoint.point,
      flange.section,
      'ONLY FLANGE',
    );
    this._currentInspectionCollection.addSurfaceElement(inspectionElement);
    inspectionElement.section = flange.section;
    if (flange instanceof StructuralElement) {
      (flange as StructuralElement).addGaugingElement(inspectionElement);
    }

    this.collectionService.addElement(inspectionElement, this._currentInspectionCollection.Date, false);

    inspectionElement.label = await this.generateInspectionLabel(inspectionElement.regionIds[0], inspectionElement.SectionId, (inspectionElement as GaugingElement).inspectionTypeId)

    this.collectionService.changeSelectElement(inspectionElement)
    resultElementsArray.push((inspectionElement as GaugingElement).elementResult)
    let clonedResultElement = await this.prepareInspectionBeforeSaving(inspectionElement as GaugingElement)
    clonedResultElementsArray.push(clonedResultElement)
    this.assetManager.tempInsertedPointsArray.push(inspectionElement);
  }

  /**
   * Exact closest point on an element's mesh surface (triangle-accurate, world space),
   * with the local triangle normal. Independent of averaged element normals, so it
   * stays correct on curved geometry where direction-based raycasts miss.
   */
  private closestPointOnElementMesh(
    element: WorkableElement,
    fromWorld: THREE.Vector3,
  ): { point: THREE.Vector3; normal: THREE.Vector3; distance: number } | null {
    const mesh = element?.SurfaceDesign as THREE.Mesh;
    const geometry = mesh?.geometry as THREE.BufferGeometry;
    const position = geometry?.attributes?.position;
    if (!position) return null;

    mesh.updateWorldMatrix(true, false);
    const fromLocal = mesh.worldToLocal(fromWorld.clone());

    const index = geometry.index;
    const triangleCount = index ? index.count / 3 : position.count / 3;
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const c = new THREE.Vector3();
    const triangle = new THREE.Triangle();
    const candidate = new THREE.Vector3();
    let bestDistanceSq = Infinity;
    let bestPoint: THREE.Vector3 = null;
    let bestNormal: THREE.Vector3 = null;

    for (let i = 0; i < triangleCount; i++) {
      const ia = index ? index.getX(i * 3) : i * 3;
      const ib = index ? index.getX(i * 3 + 1) : i * 3 + 1;
      const ic = index ? index.getX(i * 3 + 2) : i * 3 + 2;
      a.fromBufferAttribute(position as THREE.BufferAttribute, ia);
      b.fromBufferAttribute(position as THREE.BufferAttribute, ib);
      c.fromBufferAttribute(position as THREE.BufferAttribute, ic);
      triangle.set(a, b, c);
      triangle.closestPointToPoint(fromLocal, candidate);
      const distanceSq = candidate.distanceToSquared(fromLocal);
      if (distanceSq < bestDistanceSq) {
        bestDistanceSq = distanceSq;
        bestPoint = candidate.clone();
        bestNormal = triangle.getNormal(new THREE.Vector3());
      }
    }
    if (!bestPoint) return null;

    const pointWorld = mesh.localToWorld(bestPoint);
    const normalWorld = bestNormal.transformDirection(mesh.matrixWorld).normalize();
    return { point: pointWorld, normal: normalWorld, distance: pointWorld.distanceTo(fromWorld) };
  }

  /**
   * Rough web profile height: the second-largest bounding-box dimension (length >
   * height > thickness for a stiffener web). Curvature inflates the box and thus
   * the estimate — acceptable, since it is only used as a generous distance cap.
   */
  private estimatedProfileHeight(element: WorkableElement): number {
    const geometry = (element?.SurfaceDesign as THREE.Mesh)?.geometry as THREE.BufferGeometry;
    if (!geometry) return 0;
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    const size = geometry.boundingBox.getSize(new THREE.Vector3());
    const dims = [size.x, size.y, size.z].sort((d1, d2) => d2 - d1);
    return dims[1];
  }

  private getClosestFlangeIntersectionPoint(webTarget: WorkableElement, flange: WorkableElement, webPoint: THREE.Mesh) {
    const raycaster = new THREE.Raycaster();
    let direction = new THREE.Vector3();

    // raycast to flange normal direction
    direction = flange.elementNormal.clone().normalize();
    raycaster.set(webPoint.position, direction);
    // this.graphics.scene.add(new THREE.ArrowHelper(raycaster.ray.direction, raycaster.ray.origin, 100, 0xff0000));
    let intersection = raycaster.intersectObject(flange.SurfaceDesign);

    direction = flange.elementNormal.clone().negate().normalize();
    raycaster.set(webPoint.position, direction);
    // this.graphics.scene.add(new THREE.ArrowHelper(raycaster.ray.direction, raycaster.ray.origin, 100, 0xff0000));
    intersection = intersection.concat(raycaster.intersectObject(flange.SurfaceDesign));

    // raycast may need to "tilt towards" web normal to be able to intersect flange
    const calibrationConst = 0.1;
    let calibrationDirection: Vector3 = webTarget.elementNormal.clone().normalize().multiplyScalar(calibrationConst);

    direction.subVectors(flange.elementNormal, calibrationDirection).normalize();
    raycaster.set(webPoint.position, direction);
    // this.graphics.scene.add(new THREE.ArrowHelper(raycaster.ray.direction, raycaster.ray.origin, 100, 0xff0000));
    intersection = intersection.concat(raycaster.intersectObject(flange.SurfaceDesign));

    direction.subVectors(flange.elementNormal, calibrationDirection.clone().negate()).normalize();
    raycaster.set(webPoint.position, direction);
    // this.graphics.scene.add(new THREE.ArrowHelper(raycaster.ray.direction, raycaster.ray.origin, 100, 0xff0000));
    intersection = intersection.concat(raycaster.intersectObject(flange.SurfaceDesign));

    direction.subVectors(flange.elementNormal.clone().negate(), calibrationDirection).normalize();
    raycaster.set(webPoint.position, direction);
    // this.graphics.scene.add(new THREE.ArrowHelper(raycaster.ray.direction, raycaster.ray.origin, 100, 0xff0000));
    intersection = intersection.concat(raycaster.intersectObject(flange.SurfaceDesign));

    direction.subVectors(flange.elementNormal.clone().negate(), calibrationDirection.clone().negate()).normalize();
    raycaster.set(webPoint.position, direction);
    // this.graphics.scene.add(new THREE.ArrowHelper(raycaster.ray.direction, raycaster.ray.origin, 100, 0xff0000));
    intersection = intersection.concat(raycaster.intersectObject(flange.SurfaceDesign));

    return intersection;
  }

  private createElementsViewModel(
    targetElement: WorkableElement,
    collection: InspectionCampaign,
    scope: string,
    targetsIds?: Array<string>
  ): ElementResultInfo {
    let valueNormal = new Array();
    valueNormal.push(targetElement.elementNormal.x);
    valueNormal.push(targetElement.elementNormal.y);
    valueNormal.push(targetElement.elementNormal.z);

    const element: ElementResultInfo = {
      Element: null,
      IsPlanned: true,
      InsertedRegionId: targetElement.regionId,
      id: uuid() as string,
      TargetGeometryId: targetElement.Guid,
      TargetGeometryIds: targetsIds ?? [targetElement.Guid],
      TargetGeometryTypeId: targetElement.TypeId,
      InspectionMethodId: collection.CollectionInfoModel.CurrentMethodId,
      SectionId: targetElement.SectionId,
      RegionIds: targetElement.regionIds,
      AssetId: this.assetManager.asset.assetInfo.AssetId,
      Scope: scope,
      ElementInfo: {
        type: null,
        vertices: null,
        normal: valueNormal,
      }
    }
    return element
  }

  private async InsertInspectionElementMethod(ev) {
    let _inspectionMesh = this.graphicsRenderDecal.copyElementGeometry(
      ev,
      this.assetManager.asset,
      this.elementTypeForInsertionAndSelection,
      this.assetManager?.currentSection.Guid
    )

    let _target: WorkableElement = null
    for (const structureCollection of this.assetManager.asset.StructureCampaigns) {
      if (this.assetManager.currentSection.Guid) {
        _target = structureCollection.getElementById(
          this.assetManager.currentRegion.Guid,
          this.assetManager.currentSection.Guid,
          _inspectionMesh.target.name,
        )
        if (_target) break
      } else {
        _target = structureCollection.getElementByIdOnly(
          this.assetManager.currentRegion.Guid,
          _inspectionMesh.target.name,
        )
        if (_target) break
      }
    }
    let protectionTarget = null;
    if (this.assetManager.asset.ProtectionElementsCollection) {
      const protectionCollection = this.assetManager.asset.ProtectionElementsCollection
      if (this.assetManager.currentSection.Guid) {
        protectionTarget = protectionCollection.getElementById(
          this.assetManager.currentRegion.Guid,
          this.assetManager.currentSection.Guid,
          _inspectionMesh.target.name,
        )
      }
    }

    _target = protectionTarget ?? _target;

    if (_target && !(await this.confirmInsertionPlan([_target.regionId ?? this.assetManager.currentRegion?.Guid]))) return
    this.InsertNewInspectionElement(_target);
    // if (_target) {
    //   const ElementResultInfo = this.createElementsViewModel(
    //     _target,
    //     this._currentInspectionCollection,
    //     'NONE',
    //   )
    //   ElementResultInfo.CheckList = new Array<ChecklistItem>()
    //   ElementResultInfo.InspectionTypeId = this.assetManager.currentInspectionTypeId;
    //   let inspectionElement: WorkableElement = new GaugingElement(
    //     _inspectionMesh.target.clone(),
    //     this.graphicsMaterials.inspectionMaterial,
    //     [_target],
    //     new Array<ChecklistItem>(),
    //     ElementResultInfo,
    //   )
    //   this._currentInspectionCollection.addSurfaceElement(inspectionElement)
    //   this.assetManager.tempInsertedPointsArray.push(inspectionElement);
    //   this.collectionService.addElement(
    //     inspectionElement,
    //     this._currentInspectionCollection.Date,
    //     false,
    //   )

    //   let resultElementsArray: Array<ElementResultInfo> = [];
    //   let clonedResultElementsArray: Array<ElementResultInfo> = [];
    //   resultElementsArray.push((inspectionElement as GaugingElement).elementResult)
    //   let clonedResultElement = this.prepareInspectionBeforeSaving(inspectionElement as GaugingElement)
    //   clonedResultElementsArray.push(await clonedResultElement)
    //   this.saveInsertedPoints(clonedResultElementsArray, resultElementsArray)

    //   this.collectionService.changeSelectElement(inspectionElement)
    // }
  }

  public async InsertNewInspectionElement(target: WorkableElement): Promise<GaugingElement> {
    if (target) {
      const ElementResultInfo = this.createElementsViewModel(
        target,
        this._currentInspectionCollection,
        'NONE',
      )
      ElementResultInfo.CheckList = new Array<ChecklistItem>()
      ElementResultInfo.InspectionTypeId = this.assetManager.currentInspectionTypeId;
      ElementResultInfo.InspectionTypeAlias = this.assetManager.listInpectionType?.find(t => t.id === this.assetManager.currentInspectionTypeId)?.alias;
      let inspectionElement: GaugingElement = new GaugingElement(
        target.SurfaceDesign.clone(),
        GraphicsMaterials.inspectionMaterial,
        [target],
        new Array<ChecklistItem>(),
        ElementResultInfo,
      )
      this._currentInspectionCollection.addSurfaceElement(inspectionElement)
      this.assetManager.tempInsertedPointsArray.push(inspectionElement);
      this.collectionService.addElement(
        inspectionElement,
        this._currentInspectionCollection.Date,
        false,
      )

      let resultElementsArray: Array<ElementResultInfo> = [];
      let clonedResultElementsArray: Array<ElementResultInfo> = [];
      resultElementsArray.push((inspectionElement as GaugingElement).elementResult)
      let clonedResultElement = this.prepareInspectionBeforeSaving(inspectionElement as GaugingElement)
      clonedResultElementsArray.push(await clonedResultElement)
      this.saveInsertedPoints(clonedResultElementsArray, resultElementsArray)

      this.collectionService.changeSelectElement(inspectionElement);
      return inspectionElement as GaugingElement;
    }
    return null;
  }

  /**
   * Programmatic insertion variant used by the Scout POI flow.
   * Same as InsertNewInspectionElement but:
   *   - Sets the first checklist item to NOK with a supplied comment
   *     (server only persists checklist items that have a non-default
   *     value, so a follow-up photo upload by InspectionItemId would
   *     otherwise have no item to attach to).
   *   - AWAITS the server save so the caller can immediately follow up
   *     with `photoService.addInspectionItemPhoto` against the now-
   *     persisted InspectionElementId / InspectionItemId.
   */
  public async InsertNewScoutInspectionElement(
    target: WorkableElement,
    comment: string,
    inspectionTypeId: string,
    scoutImportId?: string,
    scoutSessionCode?: string,
  ): Promise<GaugingElement> {
    if (!target) return null;

    const ElementResultInfo = this.createElementsViewModel(
      target,
      this._currentInspectionCollection,
      'NONE',
    );
    ElementResultInfo.CheckList = new Array<ChecklistItem>();
    // Use the type passed in by the caller (e.g. CVI for Scout POIs) instead of the shared
    // assetManager.currentInspectionTypeId, so the import does not depend on a sideboard type-pick.
    ElementResultInfo.InspectionTypeId = inspectionTypeId;
    ElementResultInfo.InspectionTypeAlias = this.assetManager.listInpectionType?.find(t => t.id === inspectionTypeId)?.alias;
    ElementResultInfo.ScoutImportId = scoutImportId;
    ElementResultInfo.ScoutSessionCode = scoutSessionCode;
    // Force AREA regardless of the (unset) collection method — the caller may have arrived here
    // outside inspection mode. As an AREA inspection, setGeometry() serialises the target
    // element's full geometry (vertices + faces) into ElementInfo, so the POI is created with the
    // geometry of the target element and renders when the plan is viewed.
    ElementResultInfo.InspectionMethodId = INSERTIONCONSTANTS.AREA;

    const inspectionElement: GaugingElement = new GaugingElement(
      target.SurfaceDesign.clone(),
      GraphicsMaterials.inspectionMaterial,
      [target],
      new Array<ChecklistItem>(),
      ElementResultInfo,
    );
    this._currentInspectionCollection.addSurfaceElement(inspectionElement);
    this.assetManager.tempInsertedPointsArray.push(inspectionElement);
    this.collectionService.addElement(
      inspectionElement,
      this._currentInspectionCollection.Date,
      false,
    );

    const resultElementsArray: Array<ElementResultInfo> = [];
    const clonedResultElementsArray: Array<ElementResultInfo> = [];
    resultElementsArray.push(inspectionElement.elementResult);
    const clonedResultElement = await this.prepareInspectionBeforeSaving(inspectionElement);

    // Mint a stable client-side UUID for every checklist item up-front. prepareInspectionBeforeSaving
    // creates them with empty Ids and the save doesn't assign any, but the Scout image-analysis step
    // (ScoutPoiInspectorService.analyseAndApplyPoiImage) may flag ANY item — and both its anomaly
    // linkage and the photo upload need the item's Id. (clonedResultElement.CheckList references
    // inspectionElement.checklist, so mutating these items hits both copies.)
    const checklistItems = (inspectionElement.checkList ?? []) as ChecklistItem[];
    checklistItems.forEach((ckl) => {
      if (!ckl.Id) ckl.Id = uuid();
    });
    // Keep the Scout provenance note on the first item (no NOK — the image analysis now decides what
    // is wrong and applies the statuses + anomalies in analyseAndApplyPoiImage / applyScoutAnalysisAndSave).
    if (checklistItems[0]) checklistItems[0].Comments = comment;

    clonedResultElementsArray.push(clonedResultElement);

    const inspectionTypeName = this.assetManager.listInpectionType.find(
      x => x.id === clonedResultElementsArray[0]?.InspectionTypeId,
    )?.name;

    // Save the inspection with the (still empty-valued) checklist; this persists the element and its
    // checklist items server-side under the UUIDs we minted. No item is force-marked NOK here anymore —
    // the image-analysis step applies the flagged statuses + anomalies and re-saves.
    await this.collectionService.saveInspectionElement(
      clonedResultElementsArray,
      this.assetManager.referenceDate,
      inspectionTypeName,
    );

    resultElementsArray.forEach((element) => {
      element.Element.saved = true;
      element.Element.updateMaterial(this.assetManager.referenceDate);
    });
    this.persistForOfflineUse(clonedResultElementsArray);

    this.collectionService.changeSelectElement(inspectionElement);

    // Recompute the inspection element's color based on its checklist +
    // anomalies. Without this, the freshly-inserted POI inspection keeps
    // the neutral `inspectionMaterial` and gives no visual indication that
    // an anomaly exists on it. Mirrors what
    // base-inspection-element-save.command.ts:358 does after a regular UI
    // save, which routes to updateInspectionAreaColor (yellow when active
    // anomalies exist).
    this.assetManager.updateInspectionColorById(inspectionElement.Guid);

    // Notify view modes / decals / labels (anomaly-labels in particular)
    // that an inspection + anomaly was saved so they refresh in-session.
    // Without this, the newly-created anomaly only appears after a page
    // reload re-emits a `load` event via AssetManagerDownloadedInspection.
    this.inspectionEventsService.emitSaveEvent([inspectionElement]);

    return inspectionElement;
  }

  // Called by the Scout POI import after image-analysis flags checklist items (see
  // ScoutPoiInspectorService.analyseAndApplyPoiImage). The caller has already mutated the flagged
  // items' Values on inspectionElement.checkList; this creates an anomaly for each flagged item that
  // doesn't have one yet — mirroring the first-item flow in InsertNewScoutInspectionElement so the
  // uploaded photo links to an anomaly and renders on the item — then re-saves the element so the
  // applied checklist statuses persist.
  public async applyScoutAnalysisAndSave(
    inspectionElement: GaugingElement,
    flaggedItems: ChecklistItem[],
  ): Promise<void> {
    if (!inspectionElement || !flaggedItems?.length) return;

    // Create an anomaly for each flagged (non-compliant) item that doesn't have one yet.
    const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
    for (const item of flaggedItems) {
      if (item.AnomalyId && item.AnomalyId !== EMPTY_GUID) continue;
      try {
        const anomaly = this.buildAnomalyForChecklistItem(inspectionElement, item);
        await this.anomalyService.createAnomaly(anomaly).toPromise();
        item.AnomalyId = anomaly.id;
        if (!this.assetManager.asset.anomalies) {
          this.assetManager.asset.anomalies = [];
        }
        this.assetManager.asset.anomalies.push(anomaly);
      } catch (err) {
        console.warn('[applyScoutAnalysisAndSave] Anomaly create failed:', err);
      }
    }

    // prepareInspectionBeforeSaving's clone keeps CheckList by reference, so the Values the caller set
    // on inspectionElement.checkList are included in this save.
    const clonedResultElement = await this.prepareInspectionBeforeSaving(inspectionElement);
    const inspectionTypeName = this.assetManager.listInpectionType.find(
      x => x.id === inspectionElement.elementResult.InspectionTypeId,
    )?.name;

    await this.collectionService.saveInspectionElement(
      [clonedResultElement],
      this.assetManager.referenceDate,
      inspectionTypeName,
    );
    this.persistForOfflineUse([clonedResultElement]);
    this.assetManager.updateInspectionColorById(inspectionElement.Guid);
    this.inspectionEventsService.emitSaveEvent([inspectionElement]);
  }

  // Scout UTM variant of InsertInspectionPointMethod: creates a thickness
  // gauging *point* (not an element-level inspection) at an arbitrary world
  // position on the supplied target. The UTM already gives us the world
  // position; the caller has separately raycast to determine the target
  // element. Mirrors InsertInspectionPointMethod (line 351) but skips the
  // mouse-event / scope-detection branching since we already have the type.
  public async InsertNewScoutInspectionPoint(
    target: WorkableElement,
    position: THREE.Vector3,
    pointNormal: THREE.Vector3 | null,
    thicknessMm: number,
    comment: string,
    inspectionTypeId: string,
    scoutImportId?: string,
    scoutSessionCode?: string,
    batch?: { cloned: ElementResultInfo[]; seqByGroup: Map<string, number> },
  ): Promise<GaugingElement> {
    if (!target) return null;

    const typeName = ELEMENTSTYPESIDS.getElementTypeName(target.TypeId as unknown as string);
    let scope: string = 'NONE';
    switch (typeName) {
      case 'Plate':
        scope = 'ONLY PLATE';
        break;
      case 'FacePlate':
        scope = 'ONLY FACE PLATE';
        break;
      case 'BeamPart':
        scope = 'ONLY BEAM PART';
        break;
      case 'Cable':
        scope = 'ONLY CABLE';
        break;
      case 'Pipe':
        scope = 'ONLY PIPE';
        break;
      // Stiffener / Bracket / others stay 'NONE' (matches the existing flow).
    }

    const sectionNormal = (pointNormal ?? target.elementNormal?.clone())?.normalize?.() ?? pointNormal;
    const sectionBox = target.section?.box3;

    const _inspectionPoint = this.graphicsRenderDecal.createPlatePointFromPosition(
      [position.x, position.y, position.z],
      target,
      typeName,
      scope,
      sectionNormal,
      sectionBox,
    );
    if (!_inspectionPoint || !_inspectionPoint.point) {
      console.warn(`[InsertNewScoutInspectionPoint] createPlatePointFromPosition returned null for type=${typeName}`);
      return null;
    }

    const ElementResultInfo = this.createElementsViewModel(
      target,
      this._currentInspectionCollection,
      scope,
    );
    if (pointNormal) {
      ElementResultInfo.ElementInfo.normal = [pointNormal.x, pointNormal.y, pointNormal.z];
    }
    ElementResultInfo.CheckList = new Array<ChecklistItem>();
    // Use the type passed in by the caller — NOT assetManager.currentInspectionTypeId.
    // The global state is shared with the sideboard / POI flow and mutating it
    // here would corrupt a concurrent POI insertion that races for the user's
    // type pick via currentInspectionTypeId$.
    ElementResultInfo.InspectionTypeId = inspectionTypeId;
    ElementResultInfo.InspectionTypeAlias = this.assetManager.listInpectionType?.find(
      t => t.id === inspectionTypeId,
    )?.alias;
    ElementResultInfo.ScoutImportId = scoutImportId;
    ElementResultInfo.ScoutSessionCode = scoutSessionCode;
    // Force POINT regardless of the collection's CurrentMethodId — the caller
    // may have arrived here while the sideboard is in ELEMENT mode.
    ElementResultInfo.InspectionMethodId = INSERTIONCONSTANTS.POINT;

    let inspectionElement: GaugingElement;
    if (target instanceof StructuralElement) {
      inspectionElement = new SteelThicknessGaugingPoint(
        _inspectionPoint.point,
        GraphicsMaterials.pointMaterial,
        target,
        null,
        ElementResultInfo,
      );
      (target as StructuralElement).addGaugingElement(inspectionElement);
    } else {
      inspectionElement = new GaugingPoint(
        _inspectionPoint.point,
        GraphicsMaterials.pointMaterial,
        target,
        null,
        ElementResultInfo,
      );
    }

    this._currentInspectionCollection.addSurfaceElement(inspectionElement);
    this.assetManager.tempInsertedPointsArray.push(inspectionElement);
    this._lastInsertedInspection = inspectionElement;
    this.inspectionCreated$.next(inspectionElement);
    this.collectionService.addElement(
      inspectionElement,
      this._currentInspectionCollection.Date,
      false,
    );

    // In a batch, allocate the label sequence LOCALLY (one GetNextLabelSequence call per
    // region/section/type group, incremented in memory) instead of one server call per point — which
    // would also hand out duplicate numbers, since nothing is saved between points in a deferred batch.
    inspectionElement.label = batch
      ? await this.allocateBatchLabel(
          batch,
          inspectionElement.elementResult?.InsertedRegionId,
          inspectionElement.SectionId,
          inspectionElement.inspectionTypeId,
        )
      : await this.generateInspectionLabel(
          inspectionElement.elementResult?.InsertedRegionId,
          inspectionElement.SectionId,
          inspectionElement.inspectionTypeId,
        );
    this.collectionService.changeSelectElement(inspectionElement);

    const inspElement = inspectionElement.elementResult;
    if (!window.navigator.onLine) {
      inspElement.IsOfflineInspection = true;
      inspElement.OfflineInspectionStatus = OfflineInspectionStatus.Approved;
    }

    // Populate the checklist DIRECTLY (not via prepareInspectionBeforeSaving)
    // so we can guarantee the live array exists with at least one item even if
    // listInpectionType / its nested listRows haven't been loaded yet
    // (listInpectionType is only fully loaded when the user opens the
    // collections sideboard — Scout UTMs can run without that). Anything that
    // listRows provides is added; otherwise we push a single fallback item so
    // the Value has somewhere to live.
    const currentTypeForChecklist = this.assetManager.listInpectionType?.find(
      t => t.id === inspectionElement.inspectionTypeId,
    );
    const listRowsForChecklist = currentTypeForChecklist?.listRows ?? [];
    if (inspectionElement.checkList.length === 0) {
      if (listRowsForChecklist.length > 0) {
        for (const row of listRowsForChecklist) {
          inspectionElement.checkList.push({
            Id: uuid(),
            StatusId: '',
            InputTypeId: row?.inputTypeId,
            Label: row?.name,
            Value: '',
            Attachments: [],
            Status: ElementStatus.Outlined,
            Date: this.assetManager.referenceDate,
            IsLocked: false,
            AttributesList: row?.attributesList,
            InspectionSubTypeId: row?.id,
            AnomalyId: null,
            Comments: '',
            Degree: 0,
            Recommendations: '',
            Measurements: [],
            ActionPlanId: null,
            PlanIds: [],
            Clusterized: false,
          });
        }
      } else {
        // Fallback so the thickness value has a checklist item to land on.
        inspectionElement.checkList.push({
          Id: uuid(),
          StatusId: '',
          InputTypeId: '',
          Label: 'Thickness',
          Value: '',
          Attachments: [],
          Status: ElementStatus.Outlined,
          Date: this.assetManager.referenceDate,
          IsLocked: false,
          AttributesList: [],
          InspectionSubTypeId: '',
          AnomalyId: null,
          Comments: '',
          Degree: 0,
          Recommendations: '',
          Measurements: [],
          ActionPlanId: null,
          PlanIds: [],
          Clusterized: false,
        });
      }
    }
    // Explicitly alias the live elementResult.CheckList to the populated
    // _checkList. prepareInspectionBeforeSaving does this only when its own
    // populate branch runs; doing it here makes the link deterministic even
    // when listRows is unavailable. Without this alias, the form panel reads
    // elementResult.CheckList (still empty from the initial new Array()) and
    // shows nothing until a page reload reconstructs the link.
    inspectionElement.elementResult.CheckList = inspectionElement.checkList;

    const clonedResultElement = await this.prepareInspectionBeforeSaving(inspectionElement);
    const clonedResultElementsArray = [clonedResultElement];

    // Stamp the thickness reading onto every checklist item BEFORE saving so
    // the single round-trip carries the value. SteelThicknessGaugingPoint
    // reads gaugingValue from CheckList[last].Value (see
    // steelThicknessGaugingPoint.ts:189-206) and from in-memory state via
    // updateMaterial(referenceDate), which only adopts Values whose
    // item.Date <= referenceDate — that's why item.Date MUST be the
    // campaign's reference date, not `new Date()`.
    const rounded = Math.round(thicknessMm * 100) / 100;
    const valueStr = String(rounded);
    const itemDate = this.assetManager.referenceDate;
    for (const item of inspectionElement.checkList ?? []) {
      if (!item.Id) item.Id = uuid();
      item.Value = valueStr;
      // A checklist item with a value is Executed (the UI item components do this on value change; the
      // Scout insert sets the value directly, so we set the status here too — otherwise it stays Outlined).
      item.Status = ElementStatus.Executed;
      item.Date = itemDate;
      if (comment) item.Comments = comment;
      if (!Array.isArray(item.Measurements)) item.Measurements = [];
      const template = this.findMeasurementTemplate(inspectionElement, item);
      item.Measurements.push({
        id: uuid(),
        title: template?.title ?? 'UTM Thickness',
        unit: template?.unit ?? 'mm',
        value: rounded,
        date: itemDate,
      });
    }

    // Update in-memory state so view modes / decals / legends reflect the
    // reading immediately, without waiting for a reload.
    inspectionElement.gaugingValue = rounded;
    if (inspectionElement instanceof SteelThicknessGaugingPoint) {
      (inspectionElement as SteelThicknessGaugingPoint).currentThickness = rounded;
    }

    // Compute the corrosion-band color (empty / over / excessive / substantial
    // / acceptable) based on the new reading and the target plate's rule
    // thickness. The SteelThicknessGaugingPoint.gaugingValue setter has this
    // logic commented out, so the canonical entry point is
    // assetManager.setInspectionPointColor — same call used by the regular
    // user save flow.
    this.assetManager.setInspectionPointColor(inspectionElement, rounded);

    const inspectionTypeName = this.assetManager.listInpectionType.find(
      x => x.id === clonedResultElement.InspectionTypeId,
    )?.name;

    // When called as part of a batch (Scout UTM import), defer the save: hand the prepared element to
    // the caller's accumulator so it can persist every point in ONE saveInspectionElement call instead
    // of one round-trip per point. The in-memory visuals (color/material, gaugingValue) were already
    // applied above, so the point shows immediately; saveScoutInspectionPointBatch does the
    // saved-flag / updateMaterial / offline-persist / save-event for the whole group.
    if (batch) {
      batch.cloned.push(clonedResultElement);
      return inspectionElement;
    }

    // Single save with the value already populated on every checklist item.
    await this.collectionService.saveInspectionElement(
      clonedResultElementsArray,
      this.assetManager.referenceDate,
      inspectionTypeName,
    );

    inspElement.Element.saved = true;
    inspElement.Element.updateMaterial(this.assetManager.referenceDate);
    this.persistForOfflineUse(clonedResultElementsArray);

    // Notify view modes / decals / labels that an inspection was saved so
    // they refresh (anomaly labels, thickness legend, etc). Without this,
    // the value is only visible after a page reload because the relevant
    // view-mode services subscribe to inspectionEvents$ rather than polling.
    this.inspectionEventsService.emitSaveEvent([inspectionElement]);

    return inspectionElement;
  }

  // Finalizes a batch of Scout inspection points inserted with the `batch` accumulator on
  // InsertNewScoutInspectionPoint: persists them all in a SINGLE saveInspectionElement call (one
  // server round-trip for the whole group instead of one per point), then runs the post-save side
  // effects for every element.
  // Allocates the next inspection label for a batched point. Calls GetNextLabelSequence only ONCE per
  // region/section/type group (the first point), then increments the sequence in memory for the rest —
  // so a batch insert makes one label round-trip per group instead of one per point, and never hands
  // out duplicate numbers. Mirrors generateInspectionLabel's "alias N" formatting.
  private async allocateBatchLabel(
    batch: { seqByGroup: Map<string, number> },
    regionId: string,
    sectionId: string,
    inspectionTypeId: string,
  ): Promise<string> {
    if (!window.navigator.onLine) return '';

    const key = `${regionId}|${sectionId}|${inspectionTypeId}`;
    let seq = batch.seqByGroup.get(key);
    if (seq === undefined) {
      const assetId = this.assetManager.asset?.assetInfo?.AssetId;
      const baseStr = await this.collectionService.getNextLabelSequence(assetId, regionId, sectionId, inspectionTypeId);
      seq = parseInt(baseStr, 10);
      if (isNaN(seq)) return '';
    } else {
      seq = seq + 1;
    }
    batch.seqByGroup.set(key, seq);

    const alias = this.assetManager.listInpectionType?.find(i => i.id == inspectionTypeId)?.alias;
    return alias ? `${alias} ${seq}` : `${seq}`;
  }

  public async saveScoutInspectionPointBatch(
    elements: GaugingElement[],
    cloned: ElementResultInfo[],
  ): Promise<void> {
    if (!cloned.length) return;

    const inspectionTypeName = this.assetManager.listInpectionType.find(
      x => x.id === cloned[0].InspectionTypeId,
    )?.name;

    await this.collectionService.saveInspectionElement(
      cloned,
      this.assetManager.referenceDate,
      inspectionTypeName,
    );

    for (const el of elements) {
      el.elementResult.Element.saved = true;
      el.elementResult.Element.updateMaterial(this.assetManager.referenceDate);
    }
    this.persistForOfflineUse(cloned);
    this.inspectionEventsService.emitSaveEvent(elements);
  }

  // Applies a thickness reading (from a Scout UTM) to an already-inserted
  // SteelThicknessGaugingPoint, then re-saves. The canonical reading for a
  // thickness gauging point lives on the checklist item's `Value` field —
  // SteelThicknessGaugingPoint reads from CheckList[last].Value at construct
  // time and from `gaugingValue` thereafter (see steelThicknessGaugingPoint.ts
  // lines 186-206). We also push a parallel Measurement entry so the user-
  // facing measurements dialog shows the reading.
  public async appendUtmThicknessMeasurement(
    inspectionElement: GaugingElement,
    thicknessMm: number,
  ): Promise<void> {
    if (!inspectionElement.checkList || inspectionElement.checkList.length === 0) {
      console.warn('[appendUtmThicknessMeasurement] No checklist items — cannot record thickness.');
      return;
    }

    const valueStr = String(thicknessMm);
    const now = new Date();
    // Set Value on every checklist item — for THICKNESS_GAUGING the listRows
    // typically define a single row, but writing to all of them defensively
    // covers schemas that split the reading across multiple items, and also
    // matches what `gaugingValue` reads (CheckList[last].Value).
    for (const item of inspectionElement.checkList) {
      item.Value = valueStr;
      item.Status = ElementStatus.Executed; // a checklist item with a value is Executed, not Outlined
      item.Date = now;
      if (!Array.isArray(item.Measurements)) item.Measurements = [];
      // Reuse the inspection type's measurement template (so title/unit match
      // what the UI expects) when one is defined for this item, otherwise
      // fall back to a generic UTM Thickness entry.
      const template = this.findMeasurementTemplate(inspectionElement, item);
      item.Measurements.push({
        id: uuid(),
        title: template?.title ?? 'UTM Thickness',
        unit: template?.unit ?? 'mm',
        value: thicknessMm,
        date: now,
      });
    }

    // Push the live numeric value through the gauging-element setter so the
    // in-memory state (used by view modes, legends, decals) reflects the
    // reading without waiting for a reload.
    inspectionElement.gaugingValue = thicknessMm;
    if (inspectionElement instanceof SteelThicknessGaugingPoint) {
      (inspectionElement as SteelThicknessGaugingPoint).currentThickness = thicknessMm;
    }

    const cloned = await this.prepareInspectionBeforeSaving(inspectionElement);
    const inspectionTypeName = this.assetManager.listInpectionType.find(
      x => x.id === cloned.InspectionTypeId,
    )?.name;
    await this.collectionService.saveInspectionElement(
      [cloned],
      this.assetManager.referenceDate,
      inspectionTypeName,
    );
  }

  /**
   * Records a spoken (hands-free) thickness reading on an already-inserted gauging point, returning
   * everything needed to put the point back the way it was. Only the twin changes: the caller sends
   * it to the server with saveSpokenReading, in its own time, so a slow save never holds up the
   * next reading.
   *
   * Two things separate this from the Scout import path above. It writes only to the checklist
   * items dated on the reference date — creating them from the inspection type's listRows when the
   * point has no items for that date yet, and refusing when all it has there are locked — because a hands-free session runs over existing points
   * that already carry history, and stamping every item would overwrite past readings. And it
   * dates the items with the campaign's referenceDate rather than `now`, because
   * updateMaterial(referenceDate) only adopts Values whose item.Date <= referenceDate, so a
   * reading dated later would save but never appear.
   *
   * With `occurrence` — a session on a plan — the reading goes into the point's rows on that
   * day instead, and they keep their date: which plan an occurrence belongs to is decided by its
   * date alone, so re-dating it could move it into another plan. Nothing is created on that path;
   * a point with no open rows there is refused. Offline, the saved copy goes into that plan.
   */
  public applySpokenThicknessReading(
    inspectionElement: GaugingElement,
    thicknessMm: number,
    occurrence?: { day: string; planId: string },
  ): SpokenReadingUndo {
    const referenceDate = this.assetManager.referenceDate;
    const occurrenceDay = occurrence?.day;
    const rounded = Math.round(thicknessMm * 100) / 100;
    const items = occurrenceDay
      ? this.checklistItemsOnDay(inspectionElement, occurrenceDay)
      : this.checklistItemsForDate(inspectionElement, referenceDate);
    if (items.length === 0) {
      throw new SpokenReadingRefused(`No open checklist rows on ${occurrenceDay ?? calendarDay(referenceDate)} for this point.`);
    }
    const savedOn = occurrenceDay ? dayToDate(occurrenceDay) : referenceDate;
    // Backfill ids BEFORE snapshotting: the undo record finds its items by id, so an item that
    // arrived without one has to be given it now or undo would have nothing to match on.
    for (const item of items) {
      if (!item.Id) item.Id = uuid() as string;
    }

    const undo: SpokenReadingUndo = {
      // Read off the checklist, not gaugingValue: updateInspectionPointColor replaces gaugingValue
      // with the degradation curve's EstimatedThickness once a curve exists, so using it here would
      // "restore" a point to a projection it was never measured at.
      previousValue: this.lastNumericChecklistValue(items),
      items: items.map(item => ({
        itemId: item.Id,
        value: item.Value,
        status: item.Status,
        date: item.Date,
      })),
      measurementIds: [],
      savedOn,
      planId: occurrence?.planId,
    };

    const valueStr = String(rounded);
    for (const item of items) {
      item.Value = valueStr;
      // A checklist item carrying a value is Executed — the item components do this on change, and
      // a programmatic write has to do it too or the point stays Outlined with a value on it.
      item.Status = ElementStatus.Executed;
      if (!occurrenceDay) item.Date = referenceDate;
      if (!Array.isArray(item.Measurements)) item.Measurements = [];
      const template = this.findMeasurementTemplate(inspectionElement, item);
      const measurementId = uuid() as string;
      undo.measurementIds.push(measurementId);
      item.Measurements.push({
        id: measurementId,
        title: template?.title ?? 'UTM Thickness',
        unit: template?.unit ?? 'mm',
        value: rounded,
        date: item.Date,
      });
    }

    inspectionElement.elementResult.CheckList = inspectionElement.checkList;
    inspectionElement.gaugingValue = rounded;
    if (inspectionElement instanceof SteelThicknessGaugingPoint) {
      (inspectionElement as SteelThicknessGaugingPoint).currentThickness = rounded;
    }
    this.assetManager.setInspectionPointColor(inspectionElement, rounded);
    return undo;
  }

  /**
   * Puts a point back to what it held before a spoken reading; saveSpokenReading then sends that.
   * Removes the exact Measurement entries that reading pushed rather than trimming the tail, so an
   * undo after the user has since re-read the same point cannot take the wrong one out.
   */
  public revertSpokenThicknessReading(
    inspectionElement: GaugingElement,
    undo: SpokenReadingUndo,
  ): void {
    const removedIds = new Set(undo.measurementIds);

    for (const snapshot of undo.items) {
      const item = (inspectionElement.checkList ?? []).find(candidate => candidate.Id === snapshot.itemId);
      if (!item) continue;
      item.Value = snapshot.value;
      item.Status = snapshot.status;
      item.Date = snapshot.date;
      if (Array.isArray(item.Measurements)) {
        item.Measurements = item.Measurements.filter(measurement => !removedIds.has(measurement.id));
      }
    }

    inspectionElement.gaugingValue = undo.previousValue;
    if (inspectionElement instanceof SteelThicknessGaugingPoint) {
      (inspectionElement as SteelThicknessGaugingPoint).currentThickness = undo.previousValue;
    }
    this.assetManager.setInspectionPointColor(inspectionElement, undo.previousValue);
  }

  /**
   * Sends a point as it now stands — after a spoken reading, or after one was taken back.
   * `savedOn` is the day the save reports to ManagePlan, which recounts the plan whose window holds
   * it — so it has to be the reading's own day, not whatever the twin is showing. `planId` is where
   * the offline copy goes when no saved plan holds the point yet.
   */
  public async saveSpokenReading(inspectionElement: GaugingElement, savedOn: Date, planId?: string): Promise<void> {
    const cloned = await this.prepareInspectionBeforeSaving(inspectionElement);
    const inspectionTypeName = this.assetManager.listInpectionType.find(
      x => x.id === cloned.InspectionTypeId,
    )?.name;

    await this.collectionService.saveInspectionElement(
      [cloned],
      savedOn,
      inspectionTypeName,
    );

    inspectionElement.elementResult.Element.saved = true;
    inspectionElement.elementResult.Element.updateMaterial(this.assetManager.referenceDate);
    this.persistForOfflineUse([cloned], planId);
    // View modes, decals and the thickness legend listen for this; without it the reading only
    // appears after a reload.
    this.inspectionEventsService.emitSaveEvent([inspectionElement]);
  }

  /** The last real value carried by these checklist items, or undefined when none of them hold one. */
  private lastNumericChecklistValue(items: ChecklistItem[]): number | undefined {
    for (let index = items.length - 1; index >= 0; index--) {
      const raw = items[index]?.Value;
      if (raw === '' || raw === null || raw === undefined) continue;
      const value = Number(raw);
      if (!isNaN(value)) return value;
    }
    return undefined;
  }

  /**
   * The checklist items a reading on `referenceDate` belongs to, creating them from the inspection
   * type's listRows when the point has nothing on that date yet. Mirrors what the inspection form
   * does when the user moves the reference date onto a fresh date.
   */
  private checklistItemsForDate(inspectionElement: GaugingElement, referenceDate: Date): ChecklistItem[] {
    if (!Array.isArray(inspectionElement.checkList)) {
      inspectionElement.checkList = new Array<ChecklistItem>();
    }

    // calendarDay, not toDateString: rows the server dated at UTC midnight show as the day before
    // west of Greenwich, and missing them here would open a second occurrence on the same day.
    const wanted = calendarDay(referenceDate);
    const sameDay = inspectionElement.checkList.filter(item => !!item && calendarDay(item.Date as any) === wanted);
    const onDate = sameDay.filter(item => !item.IsLocked);
    if (onDate.length > 0) {
      return onDate;
    }
    // Rows on the date that are all locked are a closed occurrence. A fresh set beside them would
    // reopen that day behind the lock, so nothing is created and the caller refuses the reading.
    if (sameDay.length > 0) {
      return [];
    }

    const listRows = this.assetManager.listInpectionType?.find(
      type => type.id === inspectionElement.inspectionTypeId,
    )?.listRows ?? [];

    const rows = listRows.length > 0 ? listRows : [null];
    const created = rows.map(row => ({
      Id: uuid() as string,
      StatusId: '',
      InputTypeId: row?.inputTypeId ?? '',
      Label: row?.name ?? 'Thickness',
      Value: '',
      Attachments: [],
      Status: ElementStatus.Outlined,
      Date: referenceDate,
      IsLocked: false,
      AttributesList: row?.attributesList ?? [],
      InspectionSubTypeId: row?.id ?? '',
      AnomalyId: null,
      Comments: '',
      Degree: 0,
      Recommendations: '',
      Measurements: [],
      ActionPlanId: null,
      PlanIds: [],
      Clusterized: false,
    })) as ChecklistItem[];

    inspectionElement.checkList.push(...created);
    return created;
  }

  /**
   * A plan occurrence's open rows: the point's rows on that day that are not locked and not tied to
   * a critical area (those belong to action plans). Never creates any.
   */
  private checklistItemsOnDay(inspectionElement: GaugingElement, day: string): ChecklistItem[] {
    return (inspectionElement.checkList ?? []).filter(
      item => !!item && !item.IsLocked && !item.CriticalAreaId && calendarDay(item.Date as any) === day,
    );
  }

  // Look up the measurement template (title + unit) defined on the inspection
  // type's listRow for the given checklist item. Returns undefined if the
  // listRow has no measurement template.
  private findMeasurementTemplate(
    inspectionElement: GaugingElement,
    item: ChecklistItem,
  ): { title?: string; unit?: string } | undefined {
    const inspectionType = this.assetManager.listInpectionType?.find(
      t => t.id === inspectionElement.inspectionTypeId,
    );
    const row = inspectionType?.listRows?.find(r => r.id === item.InspectionSubTypeId)
      ?? inspectionType?.listRows?.find(r => r.name === item.Label);
    return row?.measurements?.[0];
  }

  // Mirrors AnomalyEvaluator.createAnomalyModel for the Scout POI flow.
  // We only need the minimum fields to satisfy the create endpoint and
  // give us a stable `id` to put on the checklist item + the photo upload.
  private buildAnomalyForChecklistItem(
    inspectionElement: GaugingElement,
    inspectionItem: ChecklistItem,
  ): AnomalyModel {
    const geometry: AnomalyGeometryModel = {
      typeId: inspectionElement.inspectionMethodId,
      V: inspectionElement.elementResult.ElementInfo?.vertices,
      F: inspectionElement.elementResult.ElementInfo?.faces,
    };
    return {
      id: uuid(),
      assetId: this.assetManager.asset.assetInfo.AssetId,
      regionId: inspectionElement.regionId,
      regionIds: inspectionElement.elementResult.RegionIds,
      regionName: inspectionElement.regionName,
      createdAt: inspectionItem.Date ?? new Date(),
      updatedAt: new Date(),
      description: inspectionItem.Comments ?? '',
      measurements: inspectionItem.Measurements ?? [],
      photos: [],
      sketches: [],
      sectionId: inspectionElement.section?.Guid,
      sectionName: inspectionElement.section?.Name,
      anomalyTypeName: inspectionItem.Label,
      targetId: inspectionElement.elementResult.TargetGeometryId,
      insertionMethodId: inspectionElement.inspectionMethodId,
      inspectionElementId: inspectionElement.Guid,
      anomalyGeometry: geometry,
      recommendations: inspectionItem.Recommendations ?? '',
      inspectionLabel: inspectionElement.elementResult.Label,
      targetLabels: inspectionElement.elementResult.TargetLabels,
      attributesList: inspectionItem?.AttributesList ?? [],
      inspectionElementSubTypeId: inspectionItem.InspectionSubTypeId,
    };
  }

  public async InsertNewDroneInspectionElement(target: WorkableElement, file: File, classification, confidence): Promise<GaugingElement> {
    if (target) {
      const ElementResultInfo = this.createElementsViewModel(
        target,
        this._currentInspectionCollection,
        'NONE',
      )
      ElementResultInfo.CheckList = new Array<ChecklistItem>()
      ElementResultInfo.InspectionTypeId = this.assetManager.currentInspectionTypeId;
      ElementResultInfo.InspectionTypeAlias = this.assetManager.listInpectionType?.find(t => t.id === this.assetManager.currentInspectionTypeId)?.alias;
      let inspectionElement: GaugingElement = new GaugingElement(
        target.SurfaceDesign.clone(),
        GraphicsMaterials.inspectionMaterial,
        [target],
        new Array<ChecklistItem>(),
        ElementResultInfo,
      )
      this._currentInspectionCollection.addSurfaceElement(inspectionElement)
      this.assetManager.tempInsertedPointsArray.push(inspectionElement);
      this.collectionService.addElement(
        inspectionElement,
        this._currentInspectionCollection.Date,
        false,
      )

      // let cklId = '';

      let resultElementsArray: Array<ElementResultInfo> = [];
      let clonedResultElementsArray: Array<ElementResultInfo> = [];
      resultElementsArray.push((inspectionElement as GaugingElement).elementResult)
      let clonedResultElement = this.prepareInspectionBeforeSaving(inspectionElement as GaugingElement)
      let ckl = (inspectionElement as GaugingElement).checkList.find(x => x.Label.toLowerCase() === classification.toLowerCase());
      if (ckl) {
        ckl.Value = 'NOK';
        ckl.Comments = 'Classification Levels of confidence (in order): ' + String(confidence * 100) + '% ' + '; ';
      }

      let ckl1 = (await clonedResultElement).CheckList.find(x => x.Label.toLowerCase() === classification.toLowerCase());
      if (ckl1) {
        ckl1.Value = 'NOK';
        ckl1.Comments = 'Classification Levels of confidence (in order): ' + String(confidence * 100) + '% ' + '; ';

      }
      clonedResultElementsArray.push(await clonedResultElement)
      this.saveInsertedPoints(clonedResultElementsArray, resultElementsArray);
      this.collectionService.changeSelectElement(inspectionElement);

      // let cklt = (inspectionElement as GaugingElement).checkList.find(x => x.Label.toLowerCase() === classification.toLowerCase());
      // if (cklt) {
      //   cklId = (cklt as any).Id
      // }
      // await this.uploadFiles(file, cklId);

      // ((inspectionElement as GaugingElement).elementResult as any).IsDrone = true;
      // this.collectionService.changeSelectElement(inspectionElement);

      return inspectionElement as GaugingElement;
    }
    return null;
  }

  // async uploadFiles(file: any, checklistId): Promise<void> {

  //   try {
  //     console.log('📊 ChecklistId', checklistId);
  //     const returnMediaList: DocumentMediaModel[] =
  //       await this.fileUploadService.addDocument([file], 'CheckListId', checklistId);
  //   } catch (error) {
  //   }
  // }
  //#endregion

  public async FinishInsertAreaInspection() {
    if (!Util.isNullOrUndefined(this.graphicsRenderRepair.rectd.meshs)) {
      let resultElementsArray: Array<ElementResultInfo> = [];
      let clonedResultElementsArray: Array<ElementResultInfo> = [];
      const skipVisibility = this.assetManager.asset.isNewModelMode && this.assetManager.asset.mode === 'REGIONS';
      for (let i = 0; i < this.graphicsRenderRepair.rectd.meshs.length; i++) {
        const _mesh = this.graphicsRenderRepair.rectd.meshs[i];
        const targets: any[] = this.graphicsRenderRepair.rectd.targets;
        let _targets = []

        for (let target of targets) {
          let _target: WorkableElement = null
          if (!this.assetManager.currentSection) {
            const element = this.assetManager.asset.getStructureAndRepairsElements(!skipVisibility).find(x => x.Guid === target.name);
            if (element) {
              _target = element;
            }
          }
          else {
            for (const structureCollection of this.assetManager.asset.StructureCampaigns) {
              if (this.assetManager.currentSection.Guid) {
                _target = structureCollection.getElementById(this.assetManager.currentRegion.Guid, this.assetManager.currentSection.Guid, target.name);
                if (_target) break;
              }
              else {
                _target = structureCollection.getElementByIdOnly(this.assetManager.currentRegion.Guid, target.name);
                if (_target) break;
              }
            }
          }

          if (!_target)
            if (this.assetManager.currentSection?.Guid) {
              _target = this.assetManager.asset.RepairCampaigns[0]?.getElementById(this.assetManager.currentRegion.Guid, this.assetManager.currentSection.Guid, target.name);
            }
            else {
              _target = this.assetManager.asset.RepairCampaigns[0]?.getElementByIdOnly(this.assetManager.currentRegion.Guid, target.name);
            }

          if (_target) {
            const ElementResultInfo = this.createElementsViewModel(_target, this.assetManager.__currentInspectionCollection, 'NONE');
            _targets.push(_target)

          }
        }

        if (_targets.length > 0) {
          // Every area of the drag shares its targets, so this asks at most once.
          if (!(await this.confirmInsertionPlan([_targets[0].regionId ?? this.assetManager.currentRegion?.Guid]))) {
            this.graphicsRenderRepair.rectd.meshs = []
            this.graphicsRenderRepair.rectd.targets = []
            return
          }
          const ElementResultInfo = this.createElementsViewModel(_targets[0], this.assetManager.__currentInspectionCollection, 'NONE', _targets.map(x => x.Guid));
          ElementResultInfo.CheckList = new Array<ChecklistItem>();
          ElementResultInfo.InspectionTypeId = this.assetManager.currentInspectionTypeId;
          ElementResultInfo.InspectionTypeAlias = this.assetManager.listInpectionType?.find(t => t.id === this.assetManager.currentInspectionTypeId)?.alias;
          let inspectionElement: WorkableElement = new GaugingElement(_mesh, GraphicsMaterials.inspectionMaterial,
            _targets, new Array<ChecklistItem>(), ElementResultInfo);
          this.assetManager.__currentInspectionCollection.addSurfaceElement(inspectionElement);
          this.assetManager.tempInsertedPointsArray.push(inspectionElement);
          this._lastInsertedInspection = inspectionElement as GaugingElement;
          this.inspectionCreated$.next(inspectionElement as GaugingElement);
          this.collectionService.addElement(inspectionElement, this.assetManager.__currentInspectionCollection.Date, false);

          resultElementsArray.push((inspectionElement as GaugingElement).elementResult)
          let clonedResultElement = this.prepareInspectionBeforeSaving(inspectionElement as GaugingElement)
          clonedResultElementsArray.push(await clonedResultElement)
        }
      }
      if (clonedResultElementsArray.length > 0) {
        this.saveInsertedPoints(clonedResultElementsArray, resultElementsArray)
      }
    }
    this.graphicsRenderRepair.rectd.meshs = []
    this.graphicsRenderRepair.rectd.targets = []
  }

  private async generateInspectionLabel(regionId: string, sectionId: string, inspectionTypeId: string): Promise<string> {
    let labelSequence: string = ""
    if (window.navigator.onLine) {
      let assetId = this.assetManager.asset?.assetInfo?.AssetId;
      labelSequence = await this.collectionService.getNextLabelSequence(assetId, regionId, sectionId, inspectionTypeId)
    }

    if (!labelSequence) {
      return "";
    }

    const inspectionType = this.assetManager.listInpectionType?.find(i => i.id == inspectionTypeId);

    if (inspectionType?.alias) {
      return inspectionType.alias + " " + labelSequence;
    }

    return labelSequence;
  }

  private isPositionTooCloseToExistingPoints(
    position: THREE.Vector3,
    sectionId: string,
    regionIds: Array<string>,
    pointScope: string,
    excludePointId?: string,
  ): boolean {

    const pointMappingToleranceDistanceMm = ASSET_DEFAULTS.getPointMappingToleranceDistanceMmOrDefault(this.assetManager.asset.assetInfo);
    const pointMappingToleranceDistanceMeters = pointMappingToleranceDistanceMm / 1000;
    // Get all existing inspection points in the current collection with scope filtering
    let existingPoints = this.getExistingInspectionPoints(sectionId, regionIds, excludePointId, pointScope);

    // Check distance to each existing point
    for (const existingPoint of existingPoints) {
      if(!existingPoint.element.visible) {
        continue;
      }

      const distance = position.distanceTo(existingPoint.position);
      if (distance < pointMappingToleranceDistanceMeters) {
        console.log(`Collision detected: distance ${distance.toFixed(3)}m is less than tolerance ${pointMappingToleranceDistanceMeters}m`);
        return true;
      }
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  // InstancedMesh integration helpers (REND-01, SEL-02)
  // ---------------------------------------------------------------------------

  /**
   * Map the inspection element Scope string to a PointShapeType enum value.
   *
   * Mirrors the same helper in asset-manager-downloaded-inspection.ts.
   * Fallback: any unknown scope value maps to PointShapeType.Plate.
   */
  private _scopeToShapeType(scope: string): PointShapeType {
    switch (scope) {
      case 'ONLY PLATE':      return PointShapeType.Plate;
      case 'ONLY WEB':        return PointShapeType.Web;
      case 'ONLY FLANGE':     return PointShapeType.Flange;
      case 'ONLY FACE PLATE': return PointShapeType.FacePlate;
      case 'ONLY BEAM PART':  return PointShapeType.BeamPart;
      case 'ONLY CABLE':      return PointShapeType.Cable;
      case 'ONLY PIPE':       return PointShapeType.Pipe;
      default:                return PointShapeType.Plate;
    }
  }

  /**
   * Wire a newly inserted POINT inspection element into the InstancedMesh manager.
   *
   * Called immediately after element creation at every tempInsertedPointsArray.push
   * site that creates a POINT-scope inspection (SteelThicknessGaugingPoint or GaugingPoint).
   *
   * Steps:
   *   1. Compose the world-transform matrix from the real pointMesh (position, quaternion, scale).
   *   2. Call instanceManager.addInstance — writes the slot, updates all internal maps.
   *   3. Ensure the InstancedMesh is attached to section.group (only if not already parented).
   *   4. Replace the real THREE.Mesh on SurfaceDesign with a lightweight PhantomSurfaceDesign
   *      so the 100+ existing .visible/.material access sites work without modification.
   *
   * @param element   The newly created GaugingElement.
   * @param pointMesh The real THREE.Mesh returned by the insertion helper (position/quat/scale set).
   * @param section   The Section this element belongs to.
   * @param scope     The insertion scope string used to select the correct PointShapeType batch.
   */
  private _addInstanceForPointElement(
    element: GaugingElement,
    pointMesh: THREE.Mesh,
    section: Section,
    scope: string,
  ): void {
    const shapeType = this._scopeToShapeType(scope);

    // Compose world-transform matrix from the real pointMesh.
    // Use a temporary Object3D (dummy pattern) to call updateMatrix() which
    // composes position + quaternion + scale into a single Matrix4.
    const dummy = new THREE.Object3D();
    dummy.position.copy(pointMesh.position);
    dummy.quaternion.copy(pointMesh.quaternion);
    dummy.scale.copy(pointMesh.scale);
    dummy.updateMatrix();

    const defaultColor = InspectionPointInstanceManager.getDefaultColor();
    this.instanceManager.addInstance(section, shapeType, element, dummy.matrix.clone(), defaultColor);

    // Attach the InstancedMesh to section.group if it was just created (not yet parented).
    // This mirrors the processedSections loop in asset-manager-downloaded-inspection.ts.
    const sectionMeshes = this.instanceManager.getMeshesForSection(section);
    if (sectionMeshes) {
      for (const mesh of sectionMeshes.values()) {
        if (!mesh.parent) {
          section.group.add(mesh);
        }
      }
    }

    // Replace the real SurfaceDesign mesh with a PhantomSurfaceDesign proxy.
    // The phantom stores the position from the real mesh and forwards all
    // .visible / .material writes to the InstancedMesh manager (VIS-03, REND-04).
    const phantom = new PhantomSurfaceDesign(element, this.instanceManager, pointMesh.position, pointMesh.quaternion, pointMesh.scale);
    phantom.userData = { SectionID: section.Guid, scale: pointMesh.scale.x };
    element.SurfaceDesign = phantom as unknown as THREE.Mesh;

    // Defense-in-depth: if the real mesh was already added to a scene group (e.g. by
    // an addSurfaceElement call that ran before this method), remove it — the
    // InstancedMesh instance is the only renderer of this point. The geometry is
    // built per-point (not shared), so it is safe to dispose; the material is the
    // shared GraphicsMaterials.pointMaterial and must NOT be disposed.
    if (pointMesh.parent) {
      pointMesh.parent.remove(pointMesh);
      pointMesh.geometry?.dispose();
    }
  }

  private getExistingInspectionPoints(
    sectionId: string,
    regionIds: Array<string>,
    excludePointId?: string,
    scopeFilter?: string
  ): Array<{ position: THREE.Vector3, id: string, element: GaugingElement }> {
    const existingPoints: Array<{ position: THREE.Vector3, id: string, element: GaugingElement }> = [];

    // Check all inspection campaigns
    this.assetManager.asset.InspectionCampaigns.forEach(campaign => {
      campaign.Elements.forEach(element => {
        const gaugingElement = element as GaugingElement;

        // Skip if this is the point we're excluding
        if (excludePointId && gaugingElement.Guid === excludePointId) {
          return;
        }

        // Apply scope filter if provided
        if (scopeFilter && gaugingElement.elementResult?.Scope !== scopeFilter) {
          return;
        }

        // Check if the element is in the target section and regions
        const isInTargetSection = !sectionId || gaugingElement.SectionId === sectionId;
        const isInTargetRegion = regionIds.some(regionId =>
          gaugingElement.regionIds.includes(regionId)
        );

        if (isInTargetSection && isInTargetRegion) {
          // For point inspections, use the surface design position
          if (gaugingElement.inspectionMethodId === INSERTIONCONSTANTS.POINT ||
              gaugingElement.elementResult?.ElementInfo?.vertices?.length === 3) {
            existingPoints.push({
              position: gaugingElement.SurfaceDesign.position.clone(),
              id: gaugingElement.Guid,
              element: gaugingElement
            });
          }
          // For area/element inspections, use the center of bounding box
          else if (gaugingElement.inspectionMethodId === INSERTIONCONSTANTS.AREA ||
                   gaugingElement.inspectionMethodId === INSERTIONCONSTANTS.ELEMENT) {
            const center = new THREE.Vector3();
            gaugingElement.SurfaceDesign.geometry.computeBoundingBox();
            gaugingElement.SurfaceDesign.geometry.boundingBox.getCenter(center);
            existingPoints.push({
              position: center,
              id: gaugingElement.Guid,
              element: gaugingElement
            });
          }
        }
      });
    });

    // Also check temporary inserted points that haven't been saved yet
    this.assetManager.tempInsertedPointsArray.forEach(element => {
      const gaugingElement = element as GaugingElement;

      // Skip if this is the point we're excluding
      if (excludePointId && gaugingElement.Guid === excludePointId) {
        return;
      }

      // Apply scope filter if provided
      if (scopeFilter && gaugingElement.elementResult?.Scope !== scopeFilter) {
        return;
      }

      // Check if the element is in the target section and regions
      const isInTargetSection = !sectionId || gaugingElement.SectionId === sectionId;
      const isInTargetRegion = regionIds.some(regionId =>
        gaugingElement.regionIds.includes(regionId)
      );

      if (isInTargetSection && isInTargetRegion) {
        if (gaugingElement.inspectionMethodId === INSERTIONCONSTANTS.POINT ||
            gaugingElement.elementResult?.ElementInfo?.vertices?.length === 3) {
          existingPoints.push({
            position: gaugingElement.SurfaceDesign.position.clone(),
            id: gaugingElement.Guid,
            element: gaugingElement
          });
        }
        else if (gaugingElement.inspectionMethodId === INSERTIONCONSTANTS.AREA ||
                 gaugingElement.inspectionMethodId === INSERTIONCONSTANTS.ELEMENT) {
          const center = new THREE.Vector3();
          gaugingElement.SurfaceDesign.geometry.computeBoundingBox();
          gaugingElement.SurfaceDesign.geometry.boundingBox.getCenter(center);
          existingPoints.push({
            position: center,
            id: gaugingElement.Guid,
            element: gaugingElement
          });
        }
      }
    });

    return existingPoints;
  }

  public checkForExcessiveCorrosionAndCreateAdditionalPoints(inspectionElement: GaugingElement): void {
    // Skip orphan inspections - they have no target
    if (inspectionElement.isOrphan || !inspectionElement.targetElement) return;

    if (inspectionElement.defaultMaterial === SteelThicknessGaugingPoint.excessiveCorrosionMaterial) {
      this.createAndSaveAdditionalPointsDirectly(
        {
          point: inspectionElement.SurfaceDesign,
          target: inspectionElement.targetElement
        },
        inspectionElement.section.Guid,
        inspectionElement.regionIds,
        inspectionElement.elementResult.Scope,
        inspectionElement.inspectionTypeId,
        {
          up: true,
          down: true,
          left: true,
          right: true
        }
      )
    }
  }

  public async createAndSaveAdditionalPointsDirectly(
    originalPoint: {
      point: THREE.Mesh,
      target: WorkableElement
    },
    sectionId: string,
    regionIds: Array<string>,
    scope: string,
    inspectionTypeId: string,
    directions: PointDirection
  ): Promise<void> {
    if (!originalPoint) {
      console.log('No original points provided');
      return;
    }

    const distance = ASSET_DEFAULTS.getPointMappingDistanceMmOrDefault(this.assetManager.asset.assetInfo);

    console.log('Creating additional points directly with scope:', scope);

    const originalPosition = originalPoint.point.position.clone();
    let originalTarget = originalPoint.target;

    if (!originalTarget?.Name) {
      originalTarget.Name = originalTarget.Guid;
    }

    console.log('Original position:', originalPosition);

    // Get the target element to access its geometry and normal
    let _originalTarget: WorkableElement = null;
    for (const structureCollection of this.assetManager.asset.StructureCampaigns) {
      if (sectionId) {
        for (const regionId of regionIds) {
          _originalTarget = structureCollection.getElementById(regionId, sectionId, originalTarget.Name);
          if (_originalTarget) break;
        }
        if (_originalTarget) break;
      } else {
        for (const regionId of regionIds) {
          _originalTarget = structureCollection.getElementByIdOnly(regionId, originalTarget.Name);
          if (_originalTarget) break;
        }
        if (_originalTarget) break;
      }
    }

    if (!_originalTarget) {
      // Try repair campaigns
      if (sectionId) {
        for (const regionId of regionIds) {
          _originalTarget = this.assetManager.asset.RepairCampaigns[0]?.getElementById(regionId, sectionId, originalTarget.Name);
          if (_originalTarget) break;
        }
      } else {
        for (const regionId of regionIds) {
          _originalTarget = this.assetManager.asset.RepairCampaigns[0]?.getElementByIdOnly(regionId, originalTarget.Name);
          if (_originalTarget) break;
        }
      }
    }

    if (!_originalTarget) {
      console.error('Original target element not found');
      return;
    }

    // Convert distance from mm to meters (assuming the 3D model is in meters)
    const distanceInMeters = distance / 1000;

    // Get the surface normal at the original point
    const raycaster = new THREE.Raycaster();
    const surfaceNormal = new THREE.Vector3();

    // Cast a ray from slightly above the surface to get the exact normal
    const rayOrigin = originalPosition.clone().add(_originalTarget.elementNormal.clone().multiplyScalar(0.1));
    const rayDirection = _originalTarget.elementNormal.clone().negate().normalize();
    raycaster.set(rayOrigin, rayDirection);

    const intersections = raycaster.intersectObject(_originalTarget.SurfaceDesign);
    if (intersections.length > 0) {
      surfaceNormal.copy(intersections[0].face.normal);
    } else {
      // Fallback to element normal
      surfaceNormal.copy(_originalTarget.elementNormal);
    }

    console.log('Surface normal:', surfaceNormal);

    // Create two perpendicular vectors on the surface plane
    let tempVector = new THREE.Vector3(1, 0, 0);
    if (Math.abs(surfaceNormal.dot(tempVector)) > 0.9) {
      tempVector.set(0, 1, 0);
    }

    // Create the first tangent vector (perpendicular to normal)
    const tangent1 = new THREE.Vector3().crossVectors(surfaceNormal, tempVector).normalize();

    // Create the second tangent vector (perpendicular to both normal and first tangent)
    const tangent2 = new THREE.Vector3().crossVectors(surfaceNormal, tangent1).normalize();

    // Get camera-relative directions for mapping user intentions
    const camera = this.graphics.camera;

    // Get camera's right vector (screen X direction)
    const cameraRight = new THREE.Vector3();
    camera.getWorldDirection(cameraRight);
    cameraRight.cross(camera.up).normalize();

    // Get camera's up vector (screen Y direction)
    const cameraUp = new THREE.Vector3();
    cameraUp.copy(camera.up).normalize();

    // Project camera directions onto the surface plane to determine which tangent aligns with which camera direction
    const projectedCameraRight = cameraRight.clone().sub(
      surfaceNormal.clone().multiplyScalar(cameraRight.dot(surfaceNormal))
    ).normalize();

    const projectedCameraUp = cameraUp.clone().sub(
      surfaceNormal.clone().multiplyScalar(cameraUp.dot(surfaceNormal))
    ).normalize();

    // Determine which tangent corresponds to camera up/down vs left/right
    const tangent1DotCameraUp = Math.abs(tangent1.dot(projectedCameraUp));
    const tangent1DotCameraRight = Math.abs(tangent1.dot(projectedCameraRight));

    let upDownTangent: THREE.Vector3;
    let leftRightTangent: THREE.Vector3;
    let upDownSign: number;
    let leftRightSign: number;

    if (tangent1DotCameraUp > tangent1DotCameraRight) {
      // tangent1 is more aligned with camera up/down
      upDownTangent = tangent1;
      leftRightTangent = tangent2;
      upDownSign = tangent1.dot(projectedCameraUp) > 0 ? 1 : -1;
      leftRightSign = tangent2.dot(projectedCameraRight) > 0 ? 1 : -1;
    } else {
      // tangent1 is more aligned with camera left/right
      upDownTangent = tangent2;
      leftRightTangent = tangent1;
      upDownSign = tangent2.dot(projectedCameraUp) > 0 ? 1 : -1;
      leftRightSign = tangent1.dot(projectedCameraRight) > 0 ? 1 : -1;
    }

    let targetPositions: {
      position: THREE.Vector3,
      label: string
    }[] = [];

    // Calculate target positions based on directions
    if (directions.up) {
      targetPositions.push({
        position: originalPosition.clone().add(upDownTangent.clone().multiplyScalar(upDownSign * distanceInMeters)),
        label: 'Camera Up'
      });
    }

    if (directions.down) {
      targetPositions.push({
        position: originalPosition.clone().add(upDownTangent.clone().multiplyScalar(-upDownSign * distanceInMeters)),
        label: 'Camera Down'
      });
    }

    if (directions.left) {
      targetPositions.push({
        position: originalPosition.clone().add(leftRightTangent.clone().multiplyScalar(-leftRightSign * distanceInMeters)),
        label: 'Camera Left'
      });
    }

    if (directions.right) {
      targetPositions.push({
        position: originalPosition.clone().add(leftRightTangent.clone().multiplyScalar(leftRightSign * distanceInMeters)),
        label: 'Camera Right'
      });
    }

    console.log('Target positions:', targetPositions);

    let resultElementsArray: Array<ElementResultInfo> = [];
    let clonedResultElementsArray: Array<ElementResultInfo> = [];
    let successfullyCreatedPoints = 0;

    // Create actual inspection points at each calculated position
    for (const targetPos of targetPositions) {
      try {
        console.log(`Creating inspection point at ${targetPos.label} position:`, targetPos.position);

        // Check for collision with existing points first
        if (this.isPositionTooCloseToExistingPoints(targetPos.position, sectionId, regionIds, scope)) {
          console.log(`Skipping ${targetPos.label} due to collision with existing point`);
          continue;
        }

        // Cast a ray from above the target position down to the surface
        const rayOrigin = targetPos.position.clone().add(surfaceNormal.clone().multiplyScalar(0.5));
        const rayDirection = surfaceNormal.clone().negate().normalize();
        raycaster.set(rayOrigin, rayDirection);

        // First try to intersect with the same target element
        let intersections = raycaster.intersectObject(_originalTarget.SurfaceDesign);
        let targetElement = _originalTarget;
        let intersection = intersections.length > 0 ? intersections[0] : null;

        // If no intersection with original target, try other elements in the section
        // if (!intersection) {
        //   const allElements = this.assetManager.asset.getStructureAndRepairsElements(true)
        //     .filter(
        //       el => el.SectionId === sectionId &&
        //       el.TypeId == targetElement.TypeId
        //     );

        //   for (const element of allElements) {
        //     intersections = raycaster.intersectObject(element.SurfaceDesign);
        //     if (intersections.length > 0) {
        //       intersection = intersections[0];
        //       targetElement = element;
        //       break;
        //     }
        //   }
        // }

        if (intersection && targetElement) {
          console.log(`Surface intersection found for ${targetPos.label}:`, intersection.point);

          const elementTypeName = this.geometryTypeConstants.getNameById(targetElement.TypeId.toString());

          // Create the inspection point
          const _inspectionPoint = this.graphicsRenderDecal.createPlatePointFromPosition(
            intersection.point.toArray(),
            targetElement,
            elementTypeName,
            scope,
            intersection.face.normal,
            this.assetManager.asset.GetStructureVisibleRegions()[0].sections.find(x => x.Guid === sectionId)?.box3
          );

          if (_inspectionPoint && _inspectionPoint.point) {
            // Determine the correct scope for this point
            let pointScope = scope;
            if (
              targetElement.properties?.Geometry?.WebThickness &&
              scope === 'NONE'
            ) {
              pointScope = 'ONLY WEB';
            } else if (
              targetElement.properties?.Geometry?.FlangeThickness &&
              scope === 'NONE'
            ) {
              pointScope = 'ONLY FLANGE';
            }

            const inspectionCollection = this.assetManager.asset.InspectionCampaigns[0];

            const ElementResultInfo = this.createElementsViewModel(
              targetElement,
              inspectionCollection,
              pointScope,
            );

            // Set the normal for the point
            ElementResultInfo.ElementInfo.normal = [intersection.face.normal.x, intersection.face.normal.y, intersection.face.normal.z];
            ElementResultInfo.CheckList = new Array<ChecklistItem>();
            ElementResultInfo.InspectionTypeId = inspectionTypeId;
            ElementResultInfo.InspectionTypeAlias = this.assetManager.listInpectionType?.find(t => t.id === inspectionTypeId)?.alias;
            
            // Create the inspection element
            let inspectionElement: WorkableElement = null;
            if (
              inspectionCollection.CollectionInfoModel.InspectionTypeId ===
              INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING ||
              inspectionCollection.CollectionInfoModel.CurrentInspectionTypeId ===
              INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING
            ) {
              inspectionElement = new SteelThicknessGaugingPoint(
                _inspectionPoint.point,
                GraphicsMaterials.pointMaterial,
                targetElement,
                null,
                ElementResultInfo,
              );
            } else {
              inspectionElement = new GaugingPoint(
                _inspectionPoint.point,
                GraphicsMaterials.pointMaterial,
                targetElement,
                null,
                ElementResultInfo,
              );
            }

            if (targetElement instanceof StructuralElement) {
              (targetElement as StructuralElement).addGaugingElement(inspectionElement as GaugingElement);
            }

            // NOT registered in the structure section's surfaceElements — inspection
            // elements must never appear in StructureCampaigns[0].Elements (prod parity).
            // Wire into InstancedMesh for immediate visibility (additional points, SEL-02).
            // Must run BEFORE the collection addSurfaceElement so the phantom prevents the
            // real mesh from being added to the in-scene surfaceGroup (duplicate rendering).
            this._addInstanceForPointElement(
              inspectionElement as GaugingElement,
              _inspectionPoint.point,
              targetElement.section,
              pointScope,
            );
            inspectionCollection.addSurfaceElement(inspectionElement);
            inspectionElement.section = targetElement.section;
            this.assetManager.tempInsertedPointsArray.push(inspectionElement);
            this.collectionService.addElement(
              inspectionElement,
              inspectionCollection.Date,
              false,
            );

            inspectionElement.label = await this.generateInspectionLabel(
              inspectionElement.regionIds[0],
              inspectionElement.SectionId,
              (inspectionElement as GaugingElement).inspectionTypeId
            );

            console.log(`Created inspection element for ${targetPos.label} with label:`, inspectionElement.label);

            let inspElement = (inspectionElement as GaugingElement).elementResult;
            resultElementsArray.push(inspElement);

            if (!window.navigator.onLine) {
              inspElement.IsOfflineInspection = true;
              inspElement.OfflineInspectionStatus = OfflineInspectionStatus.Approved;
            }

            let clonedResultElement = await this.prepareInspectionBeforeSaving(inspectionElement as GaugingElement);
            clonedResultElementsArray.push(clonedResultElement);

            successfullyCreatedPoints++;
          } else {
            console.warn(`Failed to create inspection point for ${targetPos.label}`);
          }
        } else {
          console.log(`No surface intersection found for ${targetPos.label}`);
        }
      } catch (error) {
        console.error(`Error creating inspection point for ${targetPos.label}:`, error);
      }
    }

    console.log(`Created ${successfullyCreatedPoints} inspection points out of ${targetPositions.length} attempted`);

    if (resultElementsArray.length > 0) {
      this.saveInsertedPoints(clonedResultElementsArray, resultElementsArray);
    }
  }
}
